import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Pencil, Trash2, X, ChevronDown, ChevronsUpDown,
  ChevronUp, Package, Wrench, Tag, ToggleLeft, ToggleRight, AlertCircle,
  Upload, CheckCircle2, FileSpreadsheet, SlidersHorizontal, Download,
  AlertTriangle, Building2, ArrowRight, Send, RefreshCw, GitMerge,
  ArrowLeftRight, Layers, AlertOctagon, CheckSquare, Users, FileText,
  Tractor, ImageOff, Camera, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';
import { formatPrice } from '../utils/format';
import { PriceInput } from '../components/PriceInput';
import { useSociete } from '../context/SocieteContext';
import { ArticleCaracTab } from '../components/CaracTab';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPES = [
  { value: 'materiel',       label: 'Matériel',       bg: 'var(--primary-50)', color: 'var(--primary-light)', icon: Tractor },
  { value: 'piece_detachee', label: 'Pièce détachée', bg: '#FFF7ED',           color: '#C2410C',              icon: Wrench  },
  { value: 'main_oeuvre',    label: "Main d'œuvre",   bg: '#F5F3FF',           color: '#7C3AED',              icon: Users   },
  { value: 'prestation',     label: 'Prestation',     bg: '#F0FDF4',           color: '#15803D',              icon: Tag     },
  { value: 'location',       label: 'Location',       bg: '#F0F9FF',           color: '#0369A1',              icon: Layers  },
];

const EMPTY_FORM = {
  reference: '', designation: '', type: 'materiel',
  famille_id: '', sous_famille_id: '',
  marque_id: '', modele_id: '',
  gestion_stock: false, quantite_min: '',
  unite_id: '', contenant_id: '',
  prix_achat: '', prix_vente: '',
  description: '', description_html: '', actif: true,
  fournisseur_prefere: null,
};

function TypeBadge({ type }) {
  const t = TYPES.find(x => x.value === type);
  if (!t) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium gap-1"
      style={{ background: t.bg, color: t.color }}>
      <t.icon size={10} /> {t.label}
    </span>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div className="bg-white rounded-xl p-4 border" style={{ borderColor: 'var(--border)' }}>
      <p className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>{value}</p>
      <p className="text-xs mt-0.5" style={{ color }}>{title}</p>
    </div>
  );
}

// ─── Column Visibility ────────────────────────────────────────────────────────
const ART_COLUMN_DEFS = [
  { key: 'reference',    label: 'Référence',        locked: true  },
  { key: 'designation',  label: 'Désignation',       locked: false },
  { key: 'type',         label: 'Type',              locked: false },
  { key: 'famille',      label: 'Famille',           locked: false },
  { key: 'marque',       label: 'Marque / Modèle',   locked: false },
  { key: 'emplacement',  label: 'Emplacement',        locked: false },
  { key: 'stock',        label: 'En stock',          locked: false },
  { key: 'unite',        label: 'Unité',             locked: false },
  { key: 'prix_vente',   label: 'P.V.',              locked: false },
  { key: 'actif',        label: 'Statut',            locked: false },
];
const ART_DEFAULT_COLS = Object.fromEntries(ART_COLUMN_DEFS.map(c => [c.key, true]));
const ART_COLS_KEY = 'articles_visible_cols_v2';
function loadArtCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(ART_COLS_KEY) || 'null');
    if (saved && typeof saved === 'object') return { ...ART_DEFAULT_COLS, ...saved, reference: true };
  } catch {}
  return { ...ART_DEFAULT_COLS };
}

// ─── StockPopover — article en ligne, dépôts en colonnes ────────────────────
function StockPopover({ article, emplacements, onClose, onTransfert, rect }) {
  const ref = useRef(null);

  const pos = rect ? {
    bottom: window.innerHeight - rect.top + 8,
    left:   Math.min(Math.max(rect.left - 24, 8), window.innerWidth - 520),
  } : null;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  // Dépôts uniques → colonnes (agence_id inclus pour le transfert)
  const depotMap = {};
  emplacements.forEach(e => {
    if (!depotMap[e.stock_id])
      depotMap[e.stock_id] = { stock_id: e.stock_id, nom: e.stock_nom, agence: e.agence_nom || '', agence_id: e.agence_id, qty: 0 };
    depotMap[e.stock_id].qty += e.quantite;
  });
  const depots = Object.values(depotMap);
  const grandTotal = depots.reduce((s, d) => s + d.qty, 0);

  if (!pos) return null;

  return (
    <div ref={ref} className="fixed z-50 bg-white rounded-xl shadow-2xl border overflow-hidden"
      style={{ bottom: pos.bottom, left: pos.left, borderColor: 'var(--border)', minWidth: 320, maxWidth: 600 }}>

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Stock disponible par dépôt
        </p>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 ml-4"><X size={13} /></button>
      </div>

      {/* Matrice : article en ligne, dépôts en colonnes */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)', borderRight: '1px solid var(--border)' }}>
                Article
              </th>
              {depots.map(d => (
                <th key={d.stock_id} className="px-4 py-2 text-center font-semibold whitespace-nowrap"
                  style={{ color: 'var(--text-muted)', borderRight: depots.length > 1 ? '1px solid var(--border)' : 'none' }}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="flex items-center gap-1"><Building2 size={10} />{d.nom}</span>
                    {d.agence && <span className="font-normal" style={{ color: 'var(--text-placeholder)' }}>{d.agence}</span>}
                  </div>
                </th>
              ))}
              {depots.length > 1 && (
                <th className="px-4 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Total</th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'white' }}>
              <td className="px-3 py-3 font-medium" style={{ borderRight: '1px solid var(--border)', color: 'var(--text-secondary)', maxWidth: 200 }}>
                <p className="font-semibold truncate" style={{ color: 'var(--text)' }}>{article.designation}</p>
                {article.reference && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{article.reference}</p>}
              </td>
              {depots.map(d => (
                <td key={d.stock_id} className="px-4 py-3 text-center"
                  style={{ borderRight: depots.length > 1 ? '1px solid var(--border)' : 'none' }}>
                  <button
                    onClick={() => onTransfert({ article, stock: d })}
                    title="Demander un transfert depuis ce dépôt"
                    className="font-bold tabular-nums text-base rounded-lg px-3 py-1.5 transition-all hover:scale-105 active:scale-95"
                    style={{ color: 'var(--primary-solid)', background: 'var(--primary-50)' }}>
                    {d.qty.toLocaleString('fr-FR')}
                  </button>
                </td>
              ))}
              {depots.length > 1 && (
                <td className="px-4 py-3 text-center font-bold text-base tabular-nums" style={{ color: 'var(--text)' }}>
                  {grandTotal.toLocaleString('fr-FR')}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-3 py-2 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
        Cliquez sur une quantité pour demander un transfert depuis ce dépôt
      </div>
    </div>
  );
}

// ─── TransfertQuickModal — confirmation transfert depuis popover stock ─────────
function TransfertQuickModal({ article, stockSource, activeAgences, onClose, navigate }) {
  const [openTransferts, setOpenTransferts] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [mode, setMode]                     = useState(null); // 'new' | transfert_id
  const [qty, setQty]                       = useState('');
  const [saving, setSaving]                 = useState(false);

  const destAgence = activeAgences?.[0];

  useEffect(() => {
    if (!stockSource?.agence_id) { setLoading(false); setMode('new'); return; }
    api.getTransferts({ statut: 'demande', agence_source_id: stockSource.agence_id, limit: 50 })
      .then(r => {
        const items = r.data?.items || r.data || [];
        // Seulement les transferts vers l'agence active de l'utilisateur
        const filtered = items.filter(t =>
          !destAgence || t.agence_dest_id === destAgence.agence_id
        );
        setOpenTransferts(filtered);
        if (filtered.length === 0) setMode('new');
      })
      .catch(() => setMode('new'))
      .finally(() => setLoading(false));
  }, [stockSource?.agence_id, destAgence?.agence_id]);

  const handleSubmit = async () => {
    if (!qty || Number(qty) <= 0) { toast.error('Quantité invalide'); return; }
    if (!mode) { toast.error('Sélectionnez un transfert ou créez-en un nouveau'); return; }
    if (mode === 'new' && !destAgence) { toast.error('Aucune agence active sélectionnée'); return; }
    setSaving(true);
    try {
      const newLigne = {
        article_id:          article.article_id,
        article_ref:         article.reference || '',
        article_designation: article.designation,
        article_type:        article.type || 'piece',
        quantite_demandee:   Number(qty),
        sequence:            0,
      };
      if (mode === 'new') {
        await api.creerTransfert({
          agence_source_id: stockSource.agence_id,
          agence_dest_id:   destAgence.agence_id,
          date_demande:     new Date().toISOString().split('T')[0],
          notes:            '',
          lignes:           [newLigne],
        });
        toast.success('Transfert créé');
      } else {
        const { data: existing } = await api.getTransfert(mode);
        const existingLignes = (existing?.lignes || []).map((l, i) => ({
          article_id:          l.article_id,
          article_ref:         l.article_ref || '',
          article_designation: l.article_designation,
          article_type:        l.article_type || 'piece',
          quantite_demandee:   l.quantite_demandee,
          sequence:            l.sequence ?? i,
        }));
        await api.updateTransfert(mode, {
          lignes: [...existingLignes, { ...newLigne, sequence: existingLignes.length }],
        });
        toast.success('Article ajouté au transfert');
      }
      onClose();
      navigate('/transferts');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div>
            <p className="font-semibold" style={{ color: 'var(--text)' }}>Demande de transfert</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{article?.designation}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Source */}
          <div className="rounded-xl p-3 border" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Source</p>
            <div className="flex items-center gap-2">
              <Building2 size={14} style={{ color: 'var(--primary-solid)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{stockSource?.nom}</p>
                {stockSource?.agence && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{stockSource.agence}</p>}
              </div>
              <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-lg"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                {stockSource?.qty?.toLocaleString('fr-FR')} dispo
              </span>
            </div>
          </div>

          {/* Quantité */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Quantité demandée
            </label>
            <input type="number" min="1" step="1" autoFocus
              value={qty} onChange={e => setQty(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: 'var(--border)' }} placeholder="Ex: 2" />
          </div>

          {/* Choix : transfert existant ou nouveau */}
          {loading ? (
            <div className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }}>
              Recherche de transferts ouverts…
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                {openTransferts.length > 0
                  ? 'Ajouter à un transfert existant ou en créer un nouveau'
                  : 'Aucun transfert ouvert — un nouveau sera créé'}
              </label>
              {openTransferts.map(t => (
                <button key={t.transfert_id} type="button"
                  onClick={() => setMode(t.transfert_id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm transition-all"
                  style={{
                    borderColor: mode === t.transfert_id ? 'var(--primary-solid)' : 'var(--border)',
                    background:  mode === t.transfert_id ? 'var(--primary-50)' : 'white',
                    color:       'var(--text)',
                  }}>
                  <span className="font-semibold">{t.numero}</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {t.lignes?.length || 0} article{(t.lignes?.length || 0) !== 1 ? 's' : ''}
                  </span>
                </button>
              ))}
              {/* Option créer nouveau */}
              <button type="button"
                onClick={() => setMode('new')}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-all"
                style={{
                  borderColor: mode === 'new' ? 'var(--primary-solid)' : 'var(--border)',
                  background:  mode === 'new' ? 'var(--primary-50)' : 'white',
                  color:       mode === 'new' ? 'var(--primary-solid)' : 'var(--text-secondary)',
                }}>
                <Plus size={14} />
                Créer un nouveau transfert
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 rounded-xl border text-sm font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Annuler
            </button>
            <button type="button" onClick={handleSubmit}
              disabled={saving || !mode || !qty || Number(qty) <= 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ background: (saving || !mode || !qty || Number(qty) <= 0) ? 'var(--text-disabled)' : 'var(--primary-solid)' }}
              data-testid="transfert-quick-confirm-btn">
              <Send size={14} />
              {saving ? 'Envoi…' : mode === 'new' ? 'Créer le transfert' : 'Ajouter au transfert'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FilterDropdown — Filtre multi-sélection avec checkboxes ─────────────────
function FilterDropdown({ label, options, selected, onChange, testId, searchable = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const n = selected.length;
  const isActive = n > 0;
  const visibleOptions = searchable && search.trim()
    ? options.filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()))
    : options;
  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button onClick={() => setOpen(v => !v)} data-testid={testId}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm whitespace-nowrap"
        style={{ borderColor: isActive ? 'var(--primary-solid)' : 'var(--text-disabled)', color: isActive ? 'var(--primary-solid)' : 'var(--text-secondary)', background: isActive ? 'var(--primary-50)' : 'white' }}>
        {label}
        {isActive && <span className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ background: 'var(--primary-solid)' }}>{n}</span>}
        <ChevronDown size={13} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border shadow-xl z-40 overflow-hidden" style={{ borderColor: 'var(--border)', minWidth: '190px' }}>
          {searchable && (
            <div className="px-2 pt-2 pb-1.5 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <input autoFocus type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                data-testid={`${testId}-search`}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border"
                style={{ borderColor: 'var(--border)', outline: 'none', background: 'var(--bg-subtle)', color: 'var(--text)' }}
                onClick={e => e.stopPropagation()} />
            </div>
          )}
          {isActive && (
            <button onClick={() => { onChange([]); setSearch(''); }} className="w-full px-3 py-1.5 text-left text-xs flex items-center gap-1.5 hover:bg-gray-50" style={{ color: 'var(--text-muted)' }}>
              <X size={10} /> Tout effacer
            </button>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {visibleOptions.length === 0
              ? <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
              : visibleOptions.map(opt => (
                <label key={opt.value} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 rounded flex-shrink-0" style={{ accentColor: 'var(--primary-solid)' }} />
                  {opt.dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: opt.dot }} />}
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{opt.label}</span>
                </label>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SearchableSelect — select à valeur unique avec recherche ─────────────────
function SearchableSelect({ value, onChange, options, placeholder = '— Sélectionner —', disabled = false, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(o => o.value === value || (o.value === null && value === null));
  const filtered = search.trim()
    ? options.filter(o => (o.label || '').toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(v => !v)}
        data-testid={testId}
        className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded border text-xs text-left truncate"
        style={{
          borderColor: 'var(--border)',
          background: disabled ? 'var(--bg-subtle)' : 'white',
          color: selected ? 'var(--text)' : 'var(--text-placeholder)',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }}
          className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border shadow-xl z-50 overflow-hidden"
          style={{ borderColor: 'var(--border)', minWidth: '160px', width: '100%' }}>
          <div className="px-2 pt-2 pb-1.5 border-b" style={{ borderColor: 'var(--border-light)' }}>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-full px-2.5 py-1 text-xs rounded border"
              style={{ borderColor: 'var(--border)', outline: 'none', background: 'var(--bg-subtle)', color: 'var(--text)' }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
            ) : filtered.map((opt, i) => (
              <button
                key={opt.value ?? `__null_${i}`}
                type="button"
                onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 transition-colors"
                style={{
                  color: (value === opt.value || (value === null && opt.value === null)) ? 'var(--primary-solid)' : 'var(--text-secondary)',
                  background: (value === opt.value || (value === null && opt.value === null)) ? 'var(--primary-50)' : 'transparent',
                  fontWeight: (value === opt.value || (value === null && opt.value === null)) ? '600' : 'normal',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── DualStockDashboard — 5 cartes cliquables (sans popover) ──────────────────

function DualStockDashboard({ article, activeCard, onCardSelect, loaded = true }) {
  const cmd     = Number(article.quantite_commandee || 0);
  const cpt     = Number(article.quantite_comptable  || 0);
  const phy     = Number(article.quantite_physique   || 0);
  const res     = Number(article.quantite_reservee   || 0);
  const dsp     = Number(article.quantite_disponible ?? article.quantite_stock ?? 0);
  const transitVal = article.en_transit != null ? Number(article.en_transit) : Math.max(0, cpt - phy);
  // null = en attente live-counts, sinon valeur réelle
  const cmdDisp = loaded ? cmd     : null;
  const resDisp = loaded ? res     : null;
  const traDisp = loaded ? transitVal : null;
  const proj    = Math.max(0, cpt) + cmd - res;

  const blocs = [
    { key: 'en-commande', label: 'En commande', value: cmdDisp,  title: 'PO achat confirmé, facture non encore reçue',          color: '#6366F1', bg: '#EEF2FF' },
    { key: 'en-transit',  label: 'En transit',  value: traDisp,  title: 'Facturé (propriété acquise) — BR non encore réceptionné', color: '#0369A1', bg: '#E0F2FE' },
    { key: 'sur-parc',    label: 'Sur Parc',    value: phy,      title: 'BR réceptionné — présent physiquement en entrepôt',     color: '#047857', bg: '#ECFDF5' },
    { key: 'reserve',     label: 'Réservé',     value: resDisp,  title: 'Engagé sur commandes clients (BL non encore livré)',    color: '#B45309', bg: '#FFFBEB' },
    { key: 'disponible',  label: 'Disponible',  value: dsp,      title: 'Vendable immédiatement (Sur Parc − Réservé)',           color: '#0F172A', bg: '#F1F5F9', bold: true },
  ];

  if (loaded && [cmd, transitVal, phy, res, dsp].every(v => v === 0)) return null;

  return (
    <div className="mb-3">
      <div className="grid grid-cols-5 gap-3 mb-1.5" style={{ padding: '0 5%' }}>
        {blocs.map(b => (
          <div key={b.key} title={b.title}
            onClick={() => onCardSelect?.(activeCard === b.key ? null : b.key)}
            className="rounded-lg px-2.5 py-2 text-center cursor-pointer transition-all select-none"
            style={{
              background: activeCard === b.key ? b.color : b.bg,
              transform: activeCard === b.key ? 'scale(1.03)' : 'scale(1)',
              outline: activeCard === b.key ? `2px solid ${b.color}` : 'none',
            }}>
            <p className="text-xs font-medium mb-0.5 truncate" style={{ color: activeCard === b.key ? 'white' : b.color }}>{b.label}</p>
            <p className={`text-base tabular-nums ${b.bold ? 'font-bold' : 'font-semibold'}`}
              style={{ color: activeCard === b.key ? 'white' : b.color }}>
              {b.value === null ? <span style={{ opacity: 0.4, fontSize: 12 }}>…</span> : b.value}
            </p>
          </div>
        ))}
      </div>
      <div className="text-right text-xs" style={{ color: 'var(--text-muted)' }}>
        Projeté (comptable + commandé − réservé) :
        <span className="ml-1 font-semibold tabular-nums" style={{ color: '#4338CA' }}>{proj}</span>
      </div>
    </div>
  );
}

// ─── StockTab — onglet Suivi de stock ─────────────────────────────────────────

const DRILL_COLS = {
  'en-commande': [
    { key: 'numero',              label: 'N° Commande' },
    { key: 'fournisseur_nom',     label: 'Fournisseur' },
    { key: 'designation',         label: 'Article' },
    { key: 'quantite_commandee',  label: 'Commandé',  num: true },
    { key: 'quantite_recue',      label: 'Reçu',      num: true },
    { key: 'date_livraison_prevue', label: 'Livraison', date: true },
  ],
  'en-transit': [
    { key: 'numero_facture',  label: 'N° Facture' },
    { key: 'fournisseur_nom', label: 'Fournisseur' },
    { key: 'designation',     label: 'Article' },
    { key: 'quantite',        label: 'Qté',       num: true },
    { key: 'date_facture',    label: 'Date',      date: true },
    { key: 'br_numero',       label: 'BR',        badge: true },
  ],
  'sur-parc': [
    { key: 'stock_nom',           label: 'Dépôt' },
    { key: 'zone_nom',            label: 'Zone' },
    { key: 'quantite_physique',   label: 'Physique',   num: true },
    { key: 'quantite_disponible', label: 'Disponible', num: true },
    { key: 'prix_moyen_pondere',  label: 'PMP €',      num: true },
    { key: 'derniere_entree',     label: 'Dernière entrée', date: true },
  ],
  'reserve': [
    { key: 'numero',              label: 'N° Vente' },
    { key: 'client',              label: 'Client' },
    { key: 'designation',         label: 'Article' },
    { key: 'quantite_commandee',  label: 'Qté',  num: true },
    { key: 'bl_numero',           label: 'BL',   badge: true },
    { key: 'statut_paiement',     label: 'Facture', badge: true },
  ],
  'disponible': [
    { key: 'stock_nom',           label: 'Dépôt' },
    { key: 'zone_nom',            label: 'Zone' },
    { key: 'quantite_disponible', label: 'Disponible', num: true },
    { key: 'quantite_physique',   label: 'Physique',   num: true },
    { key: 'quantite_reservee',   label: 'Réservé',    num: true },
    { key: 'prix_moyen_pondere',  label: 'PMP €',      num: true },
  ],
};

function StockTab({ article, articleId }) {
  const [activeCard, setActiveCard] = useState(null);
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [liveCounts, setLiveCounts] = useState(null);

  // Charger les compteurs live au montage (toujours exacts, indépendants du cache stock_articles)
  useEffect(() => {
    if (!articleId) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/stocks/articles/${articleId}/live-counts`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json())
      .then(d => setLiveCounts(d))
      .catch(() => {});
  }, [articleId]);

  useEffect(() => {
    if (!activeCard || !articleId) { setRows([]); return; }
    setLoading(true);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/stocks/articles/${articleId}/drill/${activeCard}`;
    fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json())
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [activeCard, articleId]);

  const cols = activeCard ? (DRILL_COLS[activeCard] || []) : [];
  const fmt  = (v, col) => {
    if (v == null || v === '' || v === '—') return <span style={{ color: 'var(--text-placeholder)' }}>—</span>;
    if (col.date)  return new Date(v).toLocaleDateString('fr-FR');
    if (col.num)   return <span className="tabular-nums">{Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 4 })}</span>;
    if (col.badge) return (
      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium"
        style={{ background: 'var(--bg-subtle)', color: 'var(--text-secondary)', border: '1px solid var(--border-light)' }}>
        {v}
      </span>
    );
    return v;
  };

  return (
    <div className="space-y-4">
      <DualStockDashboard
        article={liveCounts ? { ...article, ...liveCounts } : article}
        loaded={liveCounts !== null}
        activeCard={activeCard} onCardSelect={setActiveCard} />

      {activeCard && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          <div className="px-4 py-2.5 text-xs font-semibold flex items-center justify-between"
            style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
            <span>Détail — {DRILL_COLS[activeCard]?.[0] ? activeCard.replace('-', ' ').replace(/^\w/, c => c.toUpperCase()) : activeCard}</span>
            {loading && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</span>}
          </div>
          {!loading && rows.length === 0 && (
            <div className="px-4 py-6 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Aucun document</div>
          )}
          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-light)' }}>
                    {cols.map(c => (
                      <th key={c.key} className={`px-3 py-2 font-semibold ${c.num ? 'text-right' : 'text-left'}`}
                        style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      {cols.map(c => (
                        <td key={c.key} className={`px-3 py-2 ${c.num ? 'text-right tabular-nums' : ''}`}
                          style={{ color: 'var(--text)' }}>
                          {fmt(r[c.key], c)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ─── ArticleEmplacementsPanel — emplacements par dépôt/zone ───────────────────
function ArticleEmplacementsPanel({ articleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [pending, setPending] = useState({});

  // Ajout dépôt
  const [showAddDepot, setShowAddDepot] = useState(false);
  const [newStockId, setNewStockId] = useState('');
  const [newZoneId, setNewZoneId] = useState('');
  const [addingSaving, setAddingSaving] = useState(false);

  const load = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const r = await api.getArticleEmplacements(articleId);
      setData(r.data);
      const init = {};
      (r.data?.emplacements || []).forEach(e => {
        init[e.entry_id] = { zone_id: e.zone_id ?? null };
      });
      setPending(init);
    } catch { toast.error('Erreur chargement emplacements'); }
    finally { setLoading(false); }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (entryId) => {
    const p = pending[entryId];
    const original = (data?.emplacements || []).find(e => e.entry_id === entryId);
    if (!original) return;
    if ((p.zone_id ?? null) === (original.zone_id ?? null)) return;
    setSaving(entryId);
    try {
      await api.updateArticleEmplacement(articleId, entryId, { zone_id: p.zone_id ?? null });
      toast.success('Emplacement déplacé');
      await load();
    } catch { toast.error("Erreur lors du déplacement"); }
    finally { setSaving(null); }
  };

  const handleAddDepot = async () => {
    if (!newStockId) return toast.error('Sélectionnez un dépôt');
    setAddingSaving(true);
    try {
      await api.initArticleEmplacement(articleId, {
        stock_id: newStockId,
        zone_id: newZoneId || null,
      });
      toast.success('Article initialisé dans le dépôt');
      setShowAddDepot(false);
      setNewStockId('');
      setNewZoneId('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'initialisation");
    } finally {
      setAddingSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 py-3">
      <span className="animate-spin w-3.5 h-3.5 border-2 rounded-full flex-shrink-0"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement des emplacements…</span>
    </div>
  );

  if (!data) return null;

  const emplacements = data.emplacements || [];
  const zonesByStock = data.zones_by_stock || {};
  const agenceStocks = data.agence_stocks || [];

  const getZoneOptions = (stockId) => {
    const zones = (zonesByStock[stockId] || []).map(z => ({ value: z.zone_id, label: z.nom }));
    return [{ value: null, label: '— Sans zone —' }, ...zones];
  };

  // Dépôts disponibles pour l'ajout (non encore initialisés pour cet article)
  const usedStockIds = new Set(emplacements.map(e => e.stock_id));
  const availableStocks = agenceStocks.filter(s => !usedStockIds.has(s.stock_id));
  const newDepotZones = newStockId ? (zonesByStock[newStockId] || []) : [];

  return (
    <div className="space-y-2">
      {emplacements.length === 0 ? (
        <p className="text-xs px-3 py-2.5 rounded-lg"
          style={{ color: 'var(--text-placeholder)', background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
          Aucun emplacement pour cette agence.
        </p>
      ) : (
        <div className="rounded-xl border overflow-visible" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Dépôt</th>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Zone</th>
                <th className="px-3 py-2 text-center font-semibold w-14" style={{ color: 'var(--text-muted)' }}>Qté</th>
                <th className="px-2 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {emplacements.map(e => {
                const p = pending[e.entry_id] || { zone_id: e.zone_id ?? null };
                const isSaving = saving === e.entry_id;
                const hasChanges = (p.zone_id ?? null) !== (e.zone_id ?? null);
                const zoneOpts = getZoneOptions(e.stock_id);
                return (
                  <tr key={e.entry_id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="px-3 py-2">
                      <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>
                        {e.stock_nom || e.stock_id}
                        {e.agence_nom && (
                          <span className="ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>({e.agence_nom})</span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2" style={{ minWidth: 120, position: 'relative' }}>
                      <SearchableSelect
                        value={p.zone_id ?? null}
                        onChange={v => setPending(prev => ({ ...prev, [e.entry_id]: { zone_id: v } }))}
                        options={zoneOpts}
                        placeholder="— Sans zone —"
                        testId={`emplacement-zone-${e.entry_id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-center w-14">
                      <span className="font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                        {e.quantite_disponible.toLocaleString('fr-FR')}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right" style={{ minWidth: 90 }}>
                      {isSaving ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg"
                          style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                          <span className="animate-spin w-3 h-3 border-2 rounded-full flex-shrink-0"
                            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                          En cours…
                        </span>
                      ) : hasChanges ? (
                        <button type="button" onClick={() => handleSave(e.entry_id)}
                          data-testid={`emplacement-save-${e.entry_id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg transition-all"
                          style={{ background: 'var(--primary-solid)', color: 'white' }}>
                          Enregistrer
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Formulaire ajout dépôt */}
      {showAddDepot ? (
        <div className="rounded-xl border p-3 space-y-2"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
            Initialiser dans un nouveau dépôt
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={newStockId}
              onChange={e => { setNewStockId(e.target.value); setNewZoneId(''); }}
              data-testid="add-emplacement-depot-select"
              className="flex-1 min-w-0 text-xs border rounded px-2 py-1.5"
              style={{ borderColor: 'var(--border)', background: 'white' }}>
              <option value="">— Sélectionner un dépôt —</option>
              {availableStocks.map(s => (
                <option key={s.stock_id} value={s.stock_id}>
                  {s.nom}{s.agence_nom ? ` (${s.agence_nom})` : ''}
                </option>
              ))}
            </select>
            {newDepotZones.length > 0 && (
              <select
                value={newZoneId}
                onChange={e => setNewZoneId(e.target.value)}
                data-testid="add-emplacement-zone-select"
                className="flex-1 min-w-0 text-xs border rounded px-2 py-1.5"
                style={{ borderColor: 'var(--border)', background: 'white' }}>
                <option value="">— Sans zone —</option>
                {newDepotZones.map(z => (
                  <option key={z.zone_id} value={z.zone_id}>{z.nom}</option>
                ))}
              </select>
            )}
            <button
              onClick={handleAddDepot}
              disabled={addingSaving || !newStockId}
              data-testid="add-emplacement-confirm-btn"
              className="px-3 py-1.5 rounded text-white text-xs font-semibold disabled:opacity-50 transition-all"
              style={{ background: 'var(--primary-solid)' }}>
              {addingSaving ? '…' : 'Initialiser'}
            </button>
            <button
              onClick={() => { setShowAddDepot(false); setNewStockId(''); setNewZoneId(''); }}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors">
              <X size={12} />
            </button>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>
            L'article sera ajouté avec une quantité de 0. Ajustez ensuite via "Ajustements d'inventaire".
          </p>
        </div>
      ) : availableStocks.length > 0 ? (
        <button
          onClick={() => setShowAddDepot(true)}
          data-testid="add-emplacement-depot-btn"
          className="flex items-center gap-1.5 px-3 py-2 w-full text-xs border rounded-xl hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
          <Plus size={12} /> Ajouter un dépôt
        </button>
      ) : null}
    </div>
  );
}


// ─── StockThresholdsPanel — seuils min par dépôt ──────────────────────────────
function StockThresholdsPanel({ articleId, allStocks }) {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingMins, setPendingMins] = useState({});
  const [pendingMaxs, setPendingMaxs] = useState({});
  const [savingEntry, setSavingEntry] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newStockId, setNewStockId] = useState('');
  const [newMin, setNewMin] = useState('');
  const [newMax, setNewMax] = useState('');
  const [addingSaving, setAddingSaving] = useState(false);

  useEffect(() => {
    if (!articleId) return;
    setLoading(true);
    api.getStockThresholds(articleId)
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : [];
        setThresholds(data);
        const inits = {};
        const initsMax = {};
        data.forEach(t => {
          inits[t.entry_id] = t.quantite_min != null ? String(t.quantite_min) : '';
          initsMax[t.entry_id] = t.quantite_max != null ? String(t.quantite_max) : '';
        });
        setPendingMins(inits);
        setPendingMaxs(initsMax);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [articleId]);

  const handleBlurMin = async (entryId) => {
    const val = pendingMins[entryId];
    const threshold = thresholds.find(t => t.entry_id === entryId);
    const newVal = val !== '' && val != null ? Number(val) : null;
    const oldVal = threshold?.quantite_min ?? null;
    if (newVal === oldVal) return;
    setSavingEntry(entryId);
    try {
      await api.updateStockThreshold(articleId, entryId, { quantite_min: newVal, quantite_max: threshold?.quantite_max ?? null });
      setThresholds(prev => prev.map(t => t.entry_id === entryId ? { ...t, quantite_min: newVal } : t));
      toast.success('Seuil mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
      setPendingMins(prev => ({ ...prev, [entryId]: oldVal != null ? String(oldVal) : '' }));
    } finally { setSavingEntry(null); }
  };

  const handleBlurMax = async (entryId) => {
    const val = pendingMaxs[entryId];
    const threshold = thresholds.find(t => t.entry_id === entryId);
    const newVal = val !== '' && val != null ? Number(val) : null;
    const oldVal = threshold?.quantite_max ?? null;
    if (newVal === oldVal) return;
    setSavingEntry(entryId);
    try {
      await api.updateStockThreshold(articleId, entryId, { quantite_min: threshold?.quantite_min ?? null, quantite_max: newVal });
      setThresholds(prev => prev.map(t => t.entry_id === entryId ? { ...t, quantite_max: newVal } : t));
      toast.success('Seuil mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour');
      setPendingMaxs(prev => ({ ...prev, [entryId]: oldVal != null ? String(oldVal) : '' }));
    } finally { setSavingEntry(null); }
  };

  const handleAddDepot = async () => {
    if (!newStockId) return toast.error('Sélectionnez un dépôt');
    setAddingSaving(true);
    try {
      await api.createStockThreshold(articleId, {
        stock_id: newStockId,
        quantite_min: newMin !== '' ? Number(newMin) : null,
        quantite_max: newMax !== '' ? Number(newMax) : null,
      });
      const r = await api.getStockThresholds(articleId);
      const data = Array.isArray(r.data) ? r.data : [];
      setThresholds(data);
      const inits = {};
      const initsMax = {};
      data.forEach(t => {
        inits[t.entry_id] = t.quantite_min != null ? String(t.quantite_min) : '';
        initsMax[t.entry_id] = t.quantite_max != null ? String(t.quantite_max) : '';
      });
      setPendingMins(inits);
      setPendingMaxs(initsMax);
      setShowAdd(false); setNewStockId(''); setNewMin(''); setNewMax('');
      toast.success('Dépôt ajouté');
    } catch { toast.error("Erreur lors de l'ajout"); }
    finally { setAddingSaving(false); }
  };

  const existingStockIds = thresholds.map(t => t.stock_id);
  const availableStocks = allStocks.filter(s => !existingStockIds.includes(s.stock_id));

  if (loading) return (
    <div className="flex items-center gap-2 py-3">
      <span className="animate-spin w-3.5 h-3.5 border-2 rounded-full flex-shrink-0"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement des seuils…</span>
    </div>
  );

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
      {thresholds.length > 0 ? (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)' }}>Dépôt</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Stock</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Seuil min</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Seuil max</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {thresholds.map(t => {
              const isLow = t.quantite_min != null && t.quantite_disponible <= t.quantite_min;
              const isSaving = savingEntry === t.entry_id;
              return (
                <tr key={t.entry_id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td className="px-3 py-2">
                    <p className="font-medium" style={{ color: 'var(--text)' }}>{t.stock_nom}</p>
                    {t.agence_nom && <p style={{ color: 'var(--text-muted)' }}>{t.agence_nom}</p>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="font-semibold tabular-nums"
                      style={{ color: isLow ? '#DC2626' : 'var(--text)' }}>
                      {t.quantite_disponible.toLocaleString('fr-FR')}
                      {isLow && t.quantite_min != null && (
                        <span className="ml-1 font-normal" style={{ color: '#DC2626' }}>
                          / {t.quantite_min}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number" min="0" step="1"
                      value={pendingMins[t.entry_id] ?? ''}
                      onChange={e => setPendingMins(prev => ({ ...prev, [t.entry_id]: e.target.value }))}
                      onBlur={() => handleBlurMin(t.entry_id)}
                      placeholder="—"
                      data-testid={`threshold-min-${t.entry_id}`}
                      className="w-20 px-2 py-1 rounded border text-center"
                      style={{ borderColor: 'var(--border)' }}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number" min="0" step="1"
                      value={pendingMaxs[t.entry_id] ?? ''}
                      onChange={e => setPendingMaxs(prev => ({ ...prev, [t.entry_id]: e.target.value }))}
                      onBlur={() => handleBlurMax(t.entry_id)}
                      placeholder="—"
                      data-testid={`threshold-max-${t.entry_id}`}
                      className="w-20 px-2 py-1 rounded border text-center"
                      style={{ borderColor: 'var(--border)' }}
                    />
                  </td>
                  <td className="px-2 py-2 text-center w-8">
                    {isSaving && (
                      <span className="animate-spin inline-block w-3 h-3 border rounded-full"
                        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
          Aucun seuil défini — ajoutez un dépôt ci-dessous
        </p>
      )}

      {/* Ajout d'un dépôt */}
      {showAdd ? (
        <div className="flex items-center gap-2 px-3 py-2 border-t flex-wrap"
          style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
          <select value={newStockId} onChange={e => setNewStockId(e.target.value)}
            className="flex-1 min-w-0 text-xs border rounded px-2 py-1.5"
            style={{ borderColor: 'var(--border)' }}>
            <option value="">— Sélectionner un dépôt —</option>
            {availableStocks.map(s => (
              <option key={s.stock_id} value={s.stock_id}>
                {s.nom}{s.agence_nom ? ` (${s.agence_nom})` : ''}
              </option>
            ))}
          </select>
          <input type="number" min="0" step="1" placeholder="Seuil min"
            value={newMin} onChange={e => setNewMin(e.target.value)}
            className="w-24 text-xs border rounded px-2 py-1.5"
            style={{ borderColor: 'var(--border)' }} />
          <input type="number" min="0" step="1" placeholder="Seuil max"
            value={newMax} onChange={e => setNewMax(e.target.value)}
            className="w-24 text-xs border rounded px-2 py-1.5"
            style={{ borderColor: 'var(--border)' }} />
          <button onClick={handleAddDepot} disabled={addingSaving}
            className="px-3 py-1.5 rounded text-white text-xs font-semibold disabled:opacity-50"
            style={{ background: 'var(--primary-solid)' }}>
            {addingSaving ? '…' : 'Ajouter'}
          </button>
          <button onClick={() => { setShowAdd(false); setNewStockId(''); setNewMin(''); setNewMax(''); }}
            className="p-1.5 rounded hover:bg-gray-200 transition-colors">
            <X size={12} />
          </button>
        </div>
      ) : availableStocks.length > 0 ? (
        <button onClick={() => setShowAdd(true)} data-testid="add-depot-threshold-btn"
          className="flex items-center gap-1.5 px-3 py-2 w-full text-xs border-t hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
          <Plus size={12} /> Ajouter un dépôt
        </button>
      ) : null}
    </div>
  );
}

// ─── MergeArticlesPanel — fusion de 2 articles matériel ─────────────────────
function MergeArticlesPanel({ articles, onClose, onSuccess }) {
  // articles[sourceIdx] sera supprimé, articles[masterIdx] sera conservé
  const [sourceIdx, setSourceIdx] = useState(0);
  const masterIdx = sourceIdx === 0 ? 1 : 0;
  const source = articles[sourceIdx];
  const master = articles[masterIdx];

  const [preview, setPreview]           = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging]           = useState(false);

  // Réinitialiser si les articles changent (swap)
  useEffect(() => { setPreview(null); }, [sourceIdx]);

  const handlePreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await api.previewMergeArticles(source.article_id, master.article_id);
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la prévisualisation');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      await api.mergeArticles(source.article_id, master.article_id);
      toast.success(`Article "${source.designation}" fusionné dans "${master.designation}"`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la fusion');
    } finally {
      setMerging(false);
    }
  };

  return (
    <SidePanel open title="Fusion d'articles" onOpenChange={(v) => { if (!v) onClose(); }}>
      <div className="flex flex-col gap-5 p-1">

        {/* Explication */}
        <div className="rounded-xl p-3 flex items-start gap-2.5"
          style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
          <AlertOctagon size={15} className="mt-0.5 flex-shrink-0" style={{ color: '#C2410C' }} />
          <p className="text-xs leading-relaxed" style={{ color: '#7C2D12' }}>
            L'article <strong>source</strong> sera définitivement supprimé après la fusion.
            Les mouvements historiques de stock ne seront <strong>pas</strong> modifiés.
            En cas de conflit d'attributs, les attributs de l'article <strong>maître</strong> sont conservés.
          </p>
        </div>

        {/* Sélection source / maître */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
            Sens de la fusion
          </p>
          <div className="flex items-center gap-3">
            {/* Source */}
            <div className="flex-1 rounded-xl p-3 border-2 transition-all"
              style={{ borderColor: '#DC2626', background: '#FEF2F2' }}>
              <p className="text-xs font-semibold mb-0.5" style={{ color: '#DC2626' }}>
                SOURCE — sera supprimé
              </p>
              <p className="text-sm font-bold truncate" style={{ color: 'var(--text)' }}>{source.designation}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{source.reference}</p>
            </div>

            {/* Bouton swap */}
            <button
              onClick={() => setSourceIdx(idx => idx === 0 ? 1 : 0)}
              data-testid="merge-swap-btn"
              title="Inverser source et maître"
              className="flex-shrink-0 p-2 rounded-full border transition-all hover:bg-gray-100 active:scale-95"
              style={{ borderColor: 'var(--border)' }}>
              <ArrowLeftRight size={16} style={{ color: 'var(--text-muted)' }} />
            </button>

            {/* Maître */}
            <div className="flex-1 rounded-xl p-3 border-2 transition-all"
              style={{ borderColor: '#16A34A', background: '#F0FDF4' }}>
              <p className="text-xs font-semibold mb-0.5" style={{ color: '#16A34A' }}>
                MAÎTRE — sera conservé
              </p>
              <p className="text-sm font-bold truncate" style={{ color: 'var(--text)' }}>{master.designation}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{master.reference}</p>
            </div>
          </div>
        </div>

        {/* Bouton prévisualisation */}
        {!preview && (
          <button
            onClick={handlePreview}
            disabled={loadingPreview}
            data-testid="merge-preview-btn"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
            style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-solid)' }}>
            {loadingPreview
              ? <span className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary-solid)' }} />
              : <Layers size={15} />}
            {loadingPreview ? 'Analyse en cours…' : 'Voir l\'impact avant fusion'}
          </button>
        )}

        {/* Preview résultats */}
        {preview && (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="px-3 py-2 flex items-center justify-between border-b"
              style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Impact de la fusion
              </p>
              <button onClick={() => setPreview(null)}
                className="p-1 rounded hover:bg-gray-200" title="Recalculer">
                <X size={12} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-px" style={{ background: 'var(--border)' }}>
              {[
                { label: 'Matériels réassignés',     value: preview.nb_materiels,       warn: false },
                { label: 'Entrées stock transférées', value: preview.nb_stock,           warn: false },
                { label: 'Conflits stock (somme)',    value: preview.nb_conflits_stock,  warn: preview.nb_conflits_stock > 0 },
                { label: 'Ordres de réparation',      value: preview.nb_or,              warn: false },
                { label: 'Demandes de transfert',     value: preview.nb_demandes,        warn: false },
              ].map(({ label, value, warn }) => (
                <div key={label} className="px-3 py-2.5" style={{ background: 'white' }}>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</p>
                  <p className="text-xl font-bold tabular-nums mt-0.5"
                    style={{ color: warn ? '#D97706' : 'var(--text)' }}>
                    {value}
                    {warn && <AlertTriangle size={13} className="inline ml-1 mb-0.5" style={{ color: '#D97706' }} />}
                  </p>
                </div>
              ))}
            </div>
            {preview.nb_conflits_stock > 0 && (
              <p className="px-3 py-2 text-xs border-t" style={{ borderColor: 'var(--border)', color: '#92400E', background: '#FFFBEB' }}>
                {preview.nb_conflits_stock} zone(s) avec stock dans les 2 articles → les quantités seront <strong>additionnées</strong>.
              </p>
            )}
          </div>
        )}

        {/* Bouton de fusion */}
        {preview && (
          <button
            onClick={handleMerge}
            disabled={merging}
            data-testid="merge-confirm-btn"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: merging ? '#9CA3AF' : '#DC2626' }}>
            {merging
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <GitMerge size={15} />}
            {merging ? 'Fusion en cours…' : 'Exécuter la fusion'}
          </button>
        )}

      </div>
    </SidePanel>
  );
}

// ─── Onglet Description (éditeur riche) ───────────────────────────────────────
function DescriptionTab({ form, setForm, saving, onSave }) {
  const hasContent = !!(form.description_html || form.description);
  return (
    <div className="space-y-4 py-1">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
            Description commerciale
          </label>
          {hasContent && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#F0FDF4', color: '#15803D' }}>
              <FileText size={10} className="inline mr-1" />Contenu enregistré
            </span>
          )}
        </div>
        <RichTextEditor
          value={form.description_html || ''}
          onChange={html => setForm(f => ({ ...f, description_html: html }))}
          placeholder="Rédigez ici la description commerciale de l'article. Elle pourra être affichée dans les devis."
          minHeight="200px"
        />
        <p className="text-xs mt-2" style={{ color: 'var(--text-placeholder)' }}>
          Cette description sera visible dans les lignes de devis (affichage optionnel).
        </p>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function Articles() {
  const [articles, setArticles]     = useState([]);
  const [stats, setStats]           = useState(null);
  const [familles, setFamilles]     = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques]       = useState([]);
  const [modeles, setModeles]       = useState([]);
  const [unites, setUnites]         = useState([]);
  const [contenants, setContenants] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [materielsLies, setMaterielsLies] = useState([]);
  const [loadingMateriels, setLoadingMateriels] = useState(false);
  const [previsions, setPrevisions] = useState([]);
  const [loadingPrevisions, setLoadingPrevisions] = useState(false);

  // Pagination côté serveur
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [countsByType, setCountsByType] = useState({});
  const [fetchingArticles, setFetchingArticles] = useState(false);
  const [metadataReady, setMetadataReady] = useState(false);

  const [tabType, setTabType]   = useState('tous');
  const [showInactif, setShowInactif] = useState(false);
  const [search, setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortCol, setSortCol]   = useState('designation');
  const [sortDir, setSortDir]   = useState('asc');

  // Filtres avancés
  const [filterFamilles, setFilterFamillesRaw] = useState([]);
  const [filterSousFamilles, setFilterSousFamillesRaw] = useState([]);
  const [filterMarques, setFilterMarques]   = useState([]);

  // Quand les familles changent, nettoyer les sous-familles qui n'appartiennent plus
  const setFilterFamilles = useCallback((ids) => {
    setFilterFamillesRaw(ids);
    if (ids.length > 0) {
      setFilterSousFamillesRaw(prev =>
        prev.filter(sfid => {
          const sf = sousFamilles.find(s => s.sous_famille_id === sfid);
          return sf && ids.includes(sf.famille_id);
        })
      );
    }
  }, [sousFamilles]);
  const setFilterSousFamilles = setFilterSousFamillesRaw;
  const [filterStock, setFilterStock]       = useState([]);
  const [filterEnStock, setFilterEnStock]   = useState(false);

  // Emplacements stock par article
  const [emplacements, setEmplacements] = useState({});
  const { activeAgences } = useSociete();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Popover stock multi-agences
  const [stockPopover, setStockPopover]       = useState(null); // { article, emplacements, rect }
  const [transfertInit, setTransfertInit]     = useState(null); // { article, stock }
  const [allStocks, setAllStocks]             = useState([]);

  const [panelOpen, setPanelOpen] = useState(false);
  const [editRow, setEditRow]     = useState(null);
  const [panelTab, setPanelTab]   = useState('infos'); // onglet actif du panel
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [usage,  setUsage]        = useState(null);   // { peut_supprimer, raisons }

  // Prix d'achat — masqué par défaut, F10 pour toggle
  const [showPA, setShowPA] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F10') {
        e.preventDefault();
        setShowPA(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [importFile, setImportFile]           = useState(null);
  const [importing, setImporting]             = useState(false);
  const [importResult, setImportResult]       = useState(null);
  const [importDragging, setImportDragging]   = useState(false);
  const importInputRef = useRef(null);

  // Liaison matériels
  const [linking, setLinking] = useState(false);

  // Fusion articles
  const [mergePanelOpen, setMergePanelOpen] = useState(false);

  const [syncingImages, setSyncingImages] = useState(false); // eslint-disable-line no-unused-vars
  const [imagePreview, setImagePreview] = useState(null); // URL de l'image à prévisualiser

  // Sélection de lignes
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectAllRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Ref pour la recherche debounced
  const searchTimerRef = useRef(null);
  // Ref indiquant que le chargement initial (metadata) est terminé
  const metadataLoadedRef = useRef(false);

  // ── Résolution automatique du prix de vente via matrice ──────────────────
  const [resolvedPrixVente, setResolvedPrixVente] = useState(null); // { prix_vente, coeff, matrice_nom }
  const prixResolveTimerRef = useRef(null);

  const triggerResolve = useCallback(async (prixAchat, articleId) => {
    clearTimeout(prixResolveTimerRef.current);
    if (!prixAchat || parseFloat(prixAchat) <= 0) {
      setResolvedPrixVente(null);
      return;
    }
    prixResolveTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.resolveMatricePrix({
          prix_achat: parseFloat(prixAchat),
          article_id: articleId || undefined,
        });
        setResolvedPrixVente(res.data || null);
      } catch {
        setResolvedPrixVente(null);
      }
    }, 600);
  }, []);

  // Visibilité des colonnes
  const [visibleCols, setVisibleCols] = useState(loadArtCols);
  const [showColMenu, setShowColMenu] = useState(false);
  const colMenuRef = useRef(null);

  // Dropdown marque dans le formulaire
  const [marqueSearch, setMarqueSearch]   = useState('');
  const [marqueDropOpen, setMarqueDropOpen] = useState(false);
  const marqueRef = useRef(null);
  const [fournisseurs, setFournisseurs]     = useState([]);
  const [fpSearch, setFpSearch]             = useState('');
  const [fpDropOpen, setFpDropOpen]         = useState(false);
  const fpRef = useRef(null);

  // ── Chargement metadata (fait une seule fois) ───────────────────────────────
  const load = useCallback(async (reloadArticles = false) => {
    setLoading(true);
    try {
      const [statsR, famR, sfamR, mqR, modR, unR, conR, emplR, stksR] = await Promise.all([
        api.getCatalogueStats(),
        api.getFamilles(),
        api.getSousFamilles(),
        api.getMarques(),
        api.getModeles(),
        api.getUnites(),
        api.getContenants(),
        api.getArticlesEmplacements(),
        api.getStocks(),
      ]);
      setStats(statsR.data);
      setFamilles(Array.isArray(famR.data) ? famR.data : []);
      setSousFamilles(Array.isArray(sfamR.data) ? sfamR.data : []);
      setMarques(Array.isArray(mqR.data) ? mqR.data : []);
      setModeles(Array.isArray(modR.data) ? modR.data : []);
      setUnites(Array.isArray(unR.data) ? unR.data : []);
      setContenants(Array.isArray(conR.data) ? conR.data : []);
      setEmplacements(emplR.data && typeof emplR.data === 'object' ? emplR.data : {});
      setAllStocks(Array.isArray(stksR.data) ? stksR.data : []);
      metadataLoadedRef.current = true;
      setMetadataReady(true); // déclenche fetchArticles via useEffect (même si page reste à 1)
      if (reloadArticles) setPage(1); // réinitialise la page si demandé
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  // ── Chargement articles (paginé, filtré côté serveur) ────────────────────────
  const fetchArticles = useCallback(async (pg) => {
    if (!metadataLoadedRef.current) return;
    const currentPage = pg ?? page;
    setFetchingArticles(true);
    try {
      const sortMap = { reference: 'reference', type: 'type', prix_vente: 'prix_vente', stock: 'quantite_stock', famille: 'designation' };
      const params = {
        page:     currentPage,
        limit:    pageSize,
        sort_by:  sortMap[sortCol] || sortCol,
        sort_dir: sortDir === 'asc' ? 1 : -1,
      };
      if (debouncedSearch.trim())  params.q          = debouncedSearch.trim();
      if (tabType !== 'tous')       params.type        = tabType;
      if (!showInactif)             params.actif       = true;
      if (filterFamilles.length)      params.famille_ids      = filterFamilles.join(',');
      if (filterSousFamilles.length)  params.sous_famille_ids = filterSousFamilles.join(',');
      if (filterMarques.length)       params.marque_ids       = filterMarques.join(',');
      if (filterEnStock)              params.min_stock        = 1;
      if (filterStock.includes('bas')) params.stock_bas       = 1;

      const res  = await api.getArticles(params);
      const data = res.data;
      setArticles(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
      if (data.counts_by_type) setCountsByType(data.counts_by_type);
    } catch { toast.error('Erreur chargement articles'); }
    finally { setFetchingArticles(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, tabType, showInactif, sortCol, sortDir, filterFamilles, filterSousFamilles, filterMarques, filterEnStock, filterStock]);

  useEffect(() => { load(); }, [load]);

  // Debounce search → met à jour debouncedSearch après 400ms
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // Réinitialiser la page si les filtres ou la recherche changent
  useEffect(() => { setPage(1); }, [debouncedSearch, tabType, showInactif, sortCol, sortDir, filterFamilles, filterSousFamilles, filterMarques, filterEnStock]);

  // Déclenchement fetchArticles sur tout changement de paramètre côté serveur
  useEffect(() => {
    fetchArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataReady, page, pageSize, debouncedSearch, tabType, showInactif, sortCol, sortDir, filterFamilles, filterSousFamilles, filterMarques, filterEnStock]);

  // Ouverture automatique depuis ?open=<article_id> (ex: lien depuis fiche matériel)
  const autoOpenedRef = useRef(false);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const openId = searchParams.get('open');
    if (openId) {
      autoOpenedRef.current = true;
      api.getArticle(openId).then(r => { if (r.data) openEdit(r.data); }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fermer le menu colonnes au clic extérieur
  useEffect(() => {
    const h = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggleCol = (key) => setVisibleCols(v => {
    const next = { ...v, [key]: !v[key] };
    localStorage.setItem(ART_COLS_KEY, JSON.stringify(next));
    return next;
  });

  // Export Excel de TOUTES les lignes filtrées (mêmes filtres + tri que la liste)
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const handleExportXlsx = async () => {
    setExportingXlsx(true);
    try {
      const sortMap = { reference: 'reference', type: 'type', prix_vente: 'prix_vente', stock: 'quantite_stock', famille: 'designation' };
      const params = {
        sort_by:  sortMap[sortCol] || sortCol,
        sort_dir: sortDir === 'asc' ? 1 : -1,
      };
      if (debouncedSearch.trim())    params.q                = debouncedSearch.trim();
      if (tabType !== 'tous')        params.type             = tabType;
      if (!showInactif)              params.actif            = true;
      if (filterFamilles.length)     params.famille_ids      = filterFamilles.join(',');
      if (filterSousFamilles.length) params.sous_famille_ids = filterSousFamilles.join(',');
      if (filterMarques.length)      params.marque_ids       = filterMarques.join(',');
      if (filterEnStock)             params.min_stock        = 1;
      if (filterStock.includes('bas')) params.stock_bas      = 1;
      const res = await api.exportArticlesXlsx(params);
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = m ? m[1] : 'articles_export.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch { toast.error("Erreur lors de l'export Excel"); }
    finally { setExportingXlsx(false); }
  };

  const handleExportSelected = () => {
    const rows = articles.filter(a => selectedIds.has(a.article_id));
    const headers = ['Référence', 'Désignation', 'Type', 'Famille', 'Sous-famille', 'Marque', 'Modèle', 'Stock', 'P.V.', 'Actif'];
    const lines = rows.map(a => [
      a.reference, a.designation, a.type,
      getFamille(a.famille_id)?.nom || '',
      getSousFamille(a.sous_famille_id)?.nom || '',
      getMarque(a.marque_id)?.nom || '',
      getModele(a.modele_id)?.nom || '',
      a.gestion_stock ? (a.quantite_stock || 0) : '',
      a.prix_vente != null ? a.prix_vente : '',
      a.actif === false ? 'Non' : 'Oui',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'));
    const csv = [headers.join(';'), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'articles_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Fermer dropdown marque hors clic
  useEffect(() => {
    const h = (e) => { if (marqueRef.current && !marqueRef.current.contains(e.target)) setMarqueDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Fermer dropdown fournisseur hors clic + charger la liste fournisseurs
  useEffect(() => {
    const h = (e) => { if (fpRef.current && !fpRef.current.contains(e.target)) setFpDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  // Charger la liste fournisseurs au montage ET à chaque ouverture du panneau
  // (pour refléter une mise à jour du type entreprise depuis l'Annuaire)
  useEffect(() => {
    api.getEntreprises({ type_entreprise: 'fournisseur', slim: true, limit: 2000 })
      .then(r => setFournisseurs(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!panelOpen) return;
    api.getEntreprises({ type_entreprise: 'fournisseur', slim: true, limit: 2000 })
      .then(r => setFournisseurs(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, [panelOpen]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getFamillesForType = (type) => familles.filter(f => f.type === type);
  const getSousFamillesForFam = (famId) => sousFamilles.filter(sf => sf.famille_id === famId);
  const getModelesForMarque = (marqueId) => modeles.filter(m => m.marque_id === marqueId);
  const getMarque = (id) => marques.find(m => m.marque_id === id);
  const getModele = (id) => modeles.find(m => m.modele_id === id);
  const getFamille = (id) => familles.find(f => f.famille_id === id);
  const getSousFamille = (id) => sousFamilles.find(sf => sf.sous_famille_id === id);
  const getUnite = (id) => unites.find(u => u.unite_id === id);
  const getContenant = (id) => contenants.find(c => c.contenant_id === id);

  const showsMarque = (type) => type === 'materiel' || type === 'piece_detachee';
  const showsModele = (type) => type === 'materiel';
  const showsStock  = (type, gestion) => type === 'materiel' || (type === 'piece_detachee' && gestion);

  // gestion_stock effective
  const getEffectiveGestionStock = (type, override) => {
    if (type === 'materiel') return true;
    if (type === 'prestation') return false;
    return !!override;
  };

  // ── Tri / filtre ─────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };



  const handleImport = async () => {
    if (!importFile) { toast.error('Sélectionnez un fichier'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.importArticles(formData);
      setImportResult(res.data);
      toast.success(res.data.message);
      load(); fetchArticles(1); // Recharger les articles et stats
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'import';
      toast.error(msg);
      setImportResult({ error: msg });
    } finally {
      setImporting(false);
    }
  };

  const handleImportFileDrop = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['ods', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Format non supporté. Utilisez .ods ou .xlsx');
      return;
    }
    setImportFile(file);
    setImportResult(null);
  };

  const handleLinkMateriels = async () => {
    setLinking(true);
    try {
      const res = await api.linkArticlesMateriels();
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la liaison');
    } finally {
      setLinking(false);
    }
  };

  const hasActiveFilters = filterFamilles.length || filterSousFamilles.length || filterMarques.length || filterStock.length || filterEnStock;

  // filterStock 'ok' et 'non_gere' restent côté client (sur la page courante) — 'bas' est server-side
  const paged = (() => {
    let result = articles;
    const clientFilters = filterStock.filter(f => f !== 'bas');
    if (clientFilters.length > 0) {
      result = result.filter(a => {
        const artEmpl = emplacements[a.article_id] || [];
        const depotMins = artEmpl.map(e => e.quantite_min).filter(m => m != null && m > 0);
        const effectiveMin = depotMins.length > 0 ? Math.min(...depotMins) : null;
        const totalQty = artEmpl.reduce((s, e) => s + e.quantite, 0);
        const ok       = clientFilters.includes('ok')       && a.gestion_stock && (effectiveMin == null || totalQty > effectiveMin);
        const non_gere = clientFilters.includes('non_gere') && !a.gestion_stock;
        return ok || non_gere;
      });
    }
    return result;
  })();

  // ── Fusion — détection des 2 matériels sélectionnés (doit être APRÈS paged) ──
  const selectedMaterielArticles = paged
    ? [...selectedIds].map(id => paged.find(a => a.article_id === id)).filter(a => a?.type === 'materiel')
    : [];
  const canMerge = selectedIds.size === 2 && selectedMaterielArticles.length === 2;

  // safePage / totalPages viennent du serveur
  const safePage = page;

  // ── Panel ────────────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditRow(null);
    setForm({ ...EMPTY_FORM, type: tabType !== 'tous' ? tabType : 'materiel' });
    setMarqueSearch(''); setMarqueDropOpen(false);
    setPanelTab('infos');
    setResolvedPrixVente(null);
    setPanelOpen(true);
  };

  const openEdit = async (a) => {
    // Ouvrir immédiatement avec les données basiques (panneau réactif)
    setEditRow(a);
    setForm({ ...EMPTY_FORM, ...a, quantite_min: '', prix_achat: '', prix_vente: a.prix_vente ?? '' });
    setMarqueSearch(''); setMarqueDropOpen(false);
    setPanelTab('infos');
    setUsage(null);
    setResolvedPrixVente(null);
    setPanelOpen(true);
    // Charger les détails complets en arrière-plan (champs retirés de la liste)
    try {
      const { data: fullArt } = await api.getArticle(a.article_id);
      setEditRow(fullArt);
      setForm({
        ...EMPTY_FORM, ...fullArt,
        quantite_min:   fullArt.quantite_min   ?? '',
        prix_achat:     fullArt.prix_achat      ?? '',
        prix_vente:     fullArt.prix_vente      ?? '',
      });
    } catch { /* les données basiques restent affichées */ }
    // Charger l'usage (peut supprimer / archiver)
    api.getArticleUsage(a.article_id)
      .then(r => setUsage(r.data))
      .catch(() => {});
    // Charger les matériels liés si type matériel
    if (a.type === 'materiel') {
      setLoadingMateriels(true);
      setMaterielsLies([]);
      import('../api').then(({ getMateriels }) =>
        getMateriels({ slim: true, article_id: a.article_id })
          .then(r => setMaterielsLies(Array.isArray(r.data) ? r.data : []))
          .catch(() => {})
          .finally(() => setLoadingMateriels(false))
      );
    } else {
      setMaterielsLies([]);
    }
    setPanelOpen(true);
  };

  const loadPrevisions = async (articleId) => {
    setLoadingPrevisions(true);
    try {
      const { data } = await api.getArticlePrevisions(articleId);
      setPrevisions(Array.isArray(data) ? data : []);
    } catch { setPrevisions([]); }
    finally { setLoadingPrevisions(false); }
  };

  // ── Popover détail prévisions ─────────────────────────────────────────────────
  const [popover, setPopover] = useState({ open: false, type: null, agence_id: null, agence_nom: '', items: [], loading: false, rect: null });
  const navigate = useNavigate();

  const openPopover = async (type, agence_id, agence_nom, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ open: true, type, agence_id, agence_nom, items: [], loading: true, rect });
    try {
      const key = agence_id || '_';
      const fn = type === 'ventes' ? api.getArticlePrevisionVentes
               : type === 'or'     ? api.getArticlePrevisionOR
               :                     api.getArticlePrevisionAchats;
      const { data } = await fn(editRow.article_id, key);
      setPopover(p => ({ ...p, loading: false, items: Array.isArray(data) ? data : [] }));
    } catch { setPopover(p => ({ ...p, loading: false, items: [] })); }
  };

  const closePopover = () => setPopover(p => ({ ...p, open: false }));

  // ── CRUD ─────────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cet article ?')) return;
    try {
      await api.deleteArticle(id);
      toast.success('Article supprimé');
      setPanelOpen(false);
      fetchArticles();
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleArchive = async () => {
    if (!editRow) return;
    const label = editRow.actif ? 'archiver' : 'réactiver';
    if (!window.confirm(`${editRow.actif ? 'Archiver' : 'Réactiver'} cet article ?`)) return;
    try {
      await api.toggleArticleActif(editRow.article_id, !editRow.actif);
      toast.success(editRow.actif ? 'Article archivé' : 'Article réactivé');
      setPanelOpen(false);
      fetchArticles();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleActif = async (a) => {
    try {
      await api.toggleArticleActif(a.article_id, !a.actif);
      toast.success(a.actif ? 'Article archivé' : 'Article réactivé');
      fetchArticles();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleSave = async () => {
    if (!form.reference?.trim()) { toast.error('La référence est obligatoire'); return; }
    if (!form.designation?.trim()) { toast.error('La désignation est obligatoire'); return; }
    if (!form.type) { toast.error('Le type est obligatoire'); return; }
    if (!editRow && !form.unite_id) { toast.error("L'unité est obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form,
      gestion_stock: getEffectiveGestionStock(form.type, form.gestion_stock),
      quantite_min: form.quantite_min !== '' && form.quantite_min != null ? Number(form.quantite_min) : null,
      prix_achat: form.prix_achat !== '' && form.prix_achat != null ? Number(form.prix_achat) : null,
      prix_vente: form.prix_vente !== '' && form.prix_vente != null ? Number(form.prix_vente) : null,
      marque_id: showsMarque(form.type) ? (form.marque_id || null) : null,
      modele_id: showsModele(form.type) ? (form.modele_id || null) : null,
      famille_id: form.famille_id || null,
      sous_famille_id: form.sous_famille_id || null,
      fournisseur_prefere: (() => {
        const nom = String(form.fournisseur_prefere?.nom || '').trim();
        if (!nom) return null;
        // Préserver ref_fournisseur et prix (remplis par Odoo)
        const existing = editRow?.fournisseur_prefere || {};
        return { ...existing, nom };
      })(),
      unite_id: form.unite_id || null,
      contenant_id: form.contenant_id || null,
      odoo_id: form.odoo_id != null && form.odoo_id !== '' ? String(form.odoo_id) : null,
    };
    try {
      if (editRow) { await api.updateArticle(editRow.article_id, payload); toast.success('Article mis à jour'); }
      else { await api.createArticle(payload); toast.success('Article créé'); }
      setPanelOpen(false); load(true); fetchArticles(1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // ── Computed form ────────────────────────────────────────────────────────────
  const formFamilles     = getFamillesForType(form.type);
  const formSousFamilles = getSousFamillesForFam(form.famille_id);
  const formModeles      = getModelesForMarque(form.marque_id);
  const formGestionStock = getEffectiveGestionStock(form.type, form.gestion_stock);

  const filteredMarques = marques.filter(m => {
    if (!marqueSearch.trim()) return true;
    return (m.nom || '').toLowerCase().includes(marqueSearch.toLowerCase());
  });

  const selectedMarque = getMarque(form.marque_id);

  // SortIcon
  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: 'var(--primary-solid)' }} /> : <ChevronDown size={12} style={{ color: 'var(--primary-solid)' }} />;
  };

  const inp   = "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl   = "text-xs font-semibold uppercase tracking-wider flex-shrink-0";
  const lblSt = { color: 'var(--text-muted)', minWidth: '5.5rem' };

  const TABS = [
    { key: 'tous',           label: 'Tous',              count: countsByType['tous']          || 0 },
    { key: 'materiel',       label: 'Matériels',         count: countsByType['materiel']       || 0 },
    { key: 'piece_detachee', label: 'Pièces détachées',  count: countsByType['piece_detachee'] || 0 },
    { key: 'main_oeuvre',    label: "Main d'œuvre",      count: countsByType['main_oeuvre']    || 0 },
    { key: 'prestation',     label: 'Prestations',       count: countsByType['prestation']     || 0 },
    { key: 'location',       label: 'Location',          count: countsByType['location']       || 0 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // ── Composant Popover détail prévisions ───────────────────────────────────────
  const POPOVER_LABELS = { ventes: 'Commandes ventes', or: 'Ordres de réparation', achats: 'Commandes fournisseurs' };
  const POPOVER_COLORS = { ventes: '#B45309', or: '#7C3AED', achats: '#0369A1' };

  const PrevisionsPopover = () => {
    if (!popover.open) return null;
    const { rect, type, agence_nom, items, loading } = popover;
    const top = Math.min(rect.bottom + 8, window.innerHeight - 320);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 340));
    const color = POPOVER_COLORS[type];

    const handleDocClick = (item) => {
      closePopover();
      if (type === 'ventes')  navigate(`/ventes?open_devis=${item.vente_id}`);
      else if (type === 'or') navigate(`/atelier?open_or=${item.ordre_id}`);
      else                    navigate(`/achats?open_achat=${item.achat_id}`);
    };

    return createPortal(
      <>
        <div className="fixed inset-0 z-[998]" onClick={e => { e.stopPropagation(); closePopover(); }} style={{ pointerEvents: 'auto' }} />
        <div className="fixed z-[999] rounded-xl shadow-2xl border overflow-hidden"
          style={{ top, left, width: 320, background: '#ffffff', borderColor: '#E2E8F0', pointerEvents: 'auto' }}
          data-testid="previsions-popover"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-3 py-2.5 border-b"
            style={{ borderColor: 'var(--border)', background: color + '10' }}>
            <div>
              <p className="text-xs font-semibold" style={{ color }}>{POPOVER_LABELS[type]}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{agence_nom}</p>
            </div>
            <button type="button" onClick={closePopover} className="p-1 rounded-lg hover:bg-black/5 transition-colors">
              <X size={13} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs" style={{ color: 'var(--text-muted)' }}>
                <div className="animate-spin w-4 h-4 border-2 rounded-full"
                  style={{ borderColor: color, borderTopColor: 'transparent' }} />
                Chargement…
              </div>
            ) : items.length === 0 ? (
              <div className="py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>Aucun document trouvé</div>
            ) : (
              <div className="divide-y" style={{ '--tw-divide-opacity': 1 }}>
                {items.map((item, i) => (
                  <button key={i} type="button" onClick={() => handleDocClick(item)}
                    className="w-full text-left px-3 py-2.5 hover:bg-black/[0.03] transition-colors flex items-center justify-between gap-2"
                    data-testid={`popover-doc-${type}-${i}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{item.numero}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {type === 'achats' ? item.fournisseur : item.client}
                        {item.date ? <span className="ml-1" style={{ color: 'var(--text-placeholder)' }}>· {item.date}</span> : null}
                        {item.stade ? <span className="ml-1" style={{ color: 'var(--text-placeholder)' }}>· {item.stade}</span> : null}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-xs font-bold tabular-nums px-2 py-0.5 rounded-full"
                      style={{ background: color + '18', color }}>{item.quantite}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Articles</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Catalogue des articles — Matériels, Pièces détachées et Prestations</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleLinkMateriels}
            disabled={linking}
            data-testid="link-materiels-btn"
            title="Lier automatiquement les matériels aux articles (modèle = référence)"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'white' }}>
            {linking
              ? <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin inline-block" />
              : <CheckCircle2 size={15} />}
            Lier aux matériels
          </button>
          <button
            onClick={() => { setImportPanelOpen(true); setImportFile(null); setImportResult(null); }}
            data-testid="import-articles-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
            style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'white' }}>
            <Upload size={15} /> Importer
          </button>
          <button
            onClick={handleExportXlsx}
            disabled={exportingXlsx}
            data-testid="export-articles-xlsx-btn"
            title="Exporter en Excel la liste selon les filtres et le tri en cours"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ border: '1px solid var(--border)', color: 'var(--text-muted)', background: 'white' }}>
            {exportingXlsx
              ? <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin inline-block" />
              : <Download size={15} />}
            Exporter
          </button>
          <button onClick={openNew} data-testid="add-article-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={16} /> Nouvel article
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-6 gap-3">
          <StatCard title="Total articles"     value={stats.total}                         color="#555" />
          <StatCard title="Matériels"          value={stats.par_type.materiel}              color={TYPES[0].color} />
          <StatCard title="Pièces détachées"   value={stats.par_type.piece_detachee}        color={TYPES[1].color} />
          <StatCard title="Main d'œuvre"       value={stats.par_type.main_oeuvre || 0}      color={TYPES[2].color} />
          <StatCard title="Prestations"        value={stats.par_type.prestation}            color={TYPES[3].color} />
          <StatCard title="Location"           value={stats.par_type.location || 0}         color={TYPES[4].color} />
        </div>
      )}

      {/* Onglets + alerte stock bas à droite */}
      <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'var(--border-light)' }}>
        <div className="flex flex-wrap gap-1 flex-1">
          {TABS.map(tb => (
            <button key={tb.key} onClick={() => setTabType(tb.key)} data-testid={`tab-${tb.key}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{ background: tabType === tb.key ? 'white' : 'transparent', color: tabType === tb.key ? 'var(--primary-solid)' : 'var(--text-muted)', boxShadow: tabType === tb.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {tb.label}
              <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'var(--border)', color: '#555' }}>{tb.count}</span>
            </button>
          ))}
        </div>
        {/* Alerte stock bas — tout à droite */}
        {stats?.alertes_stock > 0 && (
          <button
            onClick={() => { setTabType('piece_detachee'); setFilterStock(['bas']); }}
            data-testid="stock-alert-badge"
            title="Voir les articles en stock bas"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0 transition-all hover:opacity-80"
            style={{ background: '#FEF9C3', color: '#B45309', border: '1px solid #FDE68A' }}>
            <AlertTriangle size={13} />
            {stats.alertes_stock} stock bas
          </button>
        )}
      </div>

      {/* Recherche + filtres */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Référence, désignation, marque..."
            data-testid="search-articles"
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }} />
        </div>
        {/* Famille */}
        {familles.filter(f => tabType === 'tous' || f.type === tabType).length > 0 && (
          <FilterDropdown
            testId="filter-famille"
            label="Famille"
            selected={filterFamilles}
            onChange={setFilterFamilles}
            options={familles
              .filter(f => tabType === 'tous' || f.type === tabType)
              .map(f => ({ value: f.famille_id, label: f.nom }))}
            searchable
          />
        )}
        {/* Sous-famille */}
        {(() => {
          // Familles éligibles selon le type d'onglet
          const eligibleFamIds = new Set(
            familles.filter(f => tabType === 'tous' || f.type === tabType).map(f => f.famille_id)
          );
          // Si des familles sont sélectionnées, restreindre aux leurs sous-familles
          const activeFamIds = filterFamilles.length > 0
            ? new Set(filterFamilles)
            : eligibleFamIds;
          const sfOptions = sousFamilles
            .filter(sf => activeFamIds.has(sf.famille_id))
            .map(sf => ({ value: sf.sous_famille_id, label: sf.nom }));
          if (!sfOptions.length) return null;
          return (
            <FilterDropdown
              testId="filter-sous-famille"
              label="Sous-famille"
              selected={filterSousFamilles}
              onChange={setFilterSousFamilles}
              options={sfOptions}
              searchable
            />
          );
        })()}
        {/* Marque */}
        {marques.length > 0 && (tabType === 'tous' || tabType === 'materiel' || tabType === 'piece_detachee') && (
          <FilterDropdown
            testId="filter-marque-art"
            label="Marque"
            selected={filterMarques}
            onChange={setFilterMarques}
            options={marques.map(m => ({ value: m.marque_id, label: m.nom }))}
            searchable
          />
        )}
        {/* Stock */}
        {(tabType === 'tous' || tabType === 'piece_detachee' || tabType === 'materiel') && (
          <FilterDropdown
            testId="filter-stock"
            label="En stock"
            selected={filterStock}
            onChange={setFilterStock}
            options={[
              { value: 'bas',      label: 'Stock bas',    dot: '#DC2626' },
              { value: 'ok',       label: 'Stock OK',     dot: '#16A34A' },
              { value: 'non_gere', label: 'Non géré',     dot: '#9CA3AF' },
            ]}
          />
        )}
        {/* Toggle En stock / Tous */}
        {(tabType === 'tous' || tabType === 'piece_detachee' || tabType === 'materiel') && (
          <button
            data-testid="toggle-en-stock"
            onClick={() => setFilterEnStock(v => !v)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: filterEnStock ? '#F0FDF4' : 'white',
              color: filterEnStock ? '#15803D' : 'var(--text-muted)',
              border: '1px solid',
              borderColor: filterEnStock ? '#16A34A' : 'var(--border)',
            }}>
            {filterEnStock ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            En stock
          </button>
        )}
        {!!hasActiveFilters && (
          <button
            onClick={() => { setFilterFamilles([]); setFilterSousFamillesRaw([]); setFilterMarques([]); setFilterStock([]); setFilterEnStock(false); }}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium"
            style={{ color: 'var(--text-muted)' }}>
            <X size={12} /> Réinitialiser
          </button>
        )}
        {selectedIds.size > 0 && (
          <button onClick={handleExportSelected} data-testid="export-selected-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ml-auto"
            style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid #BFDBFE' }}>
            <Download size={14} />
            Exporter ({selectedIds.size})
          </button>
        )}
        {canMerge && (
          <button
            onClick={() => setMergePanelOpen(true)}
            data-testid="merge-articles-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
            <GitMerge size={15} />
            Fusionner
          </button>
        )}
        {selectedIds.size === 2 && !canMerge && (
          <span className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
            style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
            <AlertCircle size={12} />
            La fusion nécessite 2 articles de type matériel
          </span>
        )}
        <button onClick={() => setShowInactif(v => !v)}
          data-testid="toggle-inactif"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
          style={{ background: 'white', color: 'var(--text-muted)', border: '1px solid', borderColor: 'var(--border)' }}>
          {showInactif ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          Archivés
        </button>
      </div>

      {/* Table */}
      {!fetchingArticles && paged.length === 0 ? (
        <div className="rounded-xl py-16 text-center" style={{ border: '1px dashed #E6E6E1' }}>
          <Package size={36} className="mx-auto mb-3" style={{ color: 'var(--text-disabled)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            {debouncedSearch || tabType !== 'tous' || hasActiveFilters ? 'Aucun résultat' : 'Aucun article'}
          </p>
          {!debouncedSearch && tabType === 'tous' && (
            <p className="text-xs mb-4" style={{ color: 'var(--text-placeholder)' }}>Créez votre premier article avec le bouton "Nouvel article"</p>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)', opacity: fetchingArticles ? 0.6 : 1, transition: 'opacity 0.15s' }}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                    {/* Select-all */}
                    <th className="pl-4 pr-2 py-2.5 w-10">
                      <input ref={selectAllRef} type="checkbox"
                        checked={paged.length > 0 && paged.every(a => selectedIds.has(a.article_id))}
                        onChange={() => {
                          const allSel = paged.every(a => selectedIds.has(a.article_id));
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            paged.forEach(a => allSel ? next.delete(a.article_id) : next.add(a.article_id));
                            return next;
                          });
                        }}
                        className="w-4 h-4 rounded cursor-pointer"
                        style={{ accentColor: 'var(--primary-solid)' }} />
                    </th>
                    {/* Colonne image fixe */}
                    <th className="w-10 px-1 py-2.5">
                      <div className="flex items-center justify-center">
                        <Camera size={13} style={{ color: 'var(--text-muted)' }} />
                      </div>
                    </th>
                    {[
                      { key: 'reference',   sort: 'reference',   label: 'Référence' },
                      { key: 'designation', sort: 'designation',  label: 'Désignation' },
                      { key: 'type',        sort: 'type',         label: 'Type' },
                      { key: 'famille',     sort: 'famille',      label: 'Famille' },
                      { key: 'marque',      sort: null,           label: 'Marque / Modèle' },
                      { key: 'emplacement', sort: null,           label: 'Emplacement', center: true },
                      { key: 'stock',       sort: 'stock',        label: 'En stock',    center: true },
                      { key: 'unite',       sort: null,           label: 'Unité' },
                      { key: 'prix_vente',  sort: 'prix_vente',   label: 'P.V.' },
                      { key: 'actif',       sort: null,           label: 'Actif' },
                    ].filter(c => visibleCols[c.key] && (c.key !== 'type' || tabType === 'tous')).map(col => (
                      <th key={col.key}
                        onClick={col.sort ? () => handleSort(col.sort) : undefined}
                        className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${col.center ? 'text-center' : 'text-left'}`}
                        style={{ color: 'var(--text-muted)', cursor: col.sort ? 'pointer' : 'default', userSelect: 'none' }}>
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {col.sort && <SortIcon col={col.sort} />}
                        </span>
                      </th>
                    ))}
                    {/* Sélecteur de colonnes dans le <th> */}
                    <th className="w-10 px-2 py-2.5 text-right" style={{ background: 'var(--bg-subtle)' }}>
                      <div className="relative flex justify-end" ref={colMenuRef}>
                        <button data-testid="art-col-picker-btn" onClick={() => setShowColMenu(v => !v)}
                          title="Choisir les colonnes"
                          className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                          style={{ color: 'var(--text-muted)' }}>
                          <SlidersHorizontal size={14} />
                        </button>
                        {showColMenu && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border shadow-xl z-40 py-1.5"
                            style={{ borderColor: 'var(--border)', minWidth: '190px' }}>
                            {ART_COLUMN_DEFS.map(col => (
                              <label key={col.key}
                                className={`flex items-center gap-2.5 px-3 py-1.5 rounded ${col.locked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}>
                                <input type="checkbox" checked={!!visibleCols[col.key]}
                                  onChange={() => { if (!col.locked) toggleCol(col.key); }}
                                  disabled={col.locked}
                                  className="w-3.5 h-3.5 rounded"
                                  style={{ accentColor: 'var(--primary-solid)' }} />
                                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{col.label}</span>
                                {col.locked && <span className="ml-auto text-xs" style={{ color: 'var(--text-disabled)' }}>fixe</span>}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((a, idx) => {
                    const marque = getMarque(a.marque_id);
                    const modele = getModele(a.modele_id);
                    const famille = getFamille(a.famille_id);
                    const sousFam = getSousFamille(a.sous_famille_id);
                    const unite = getUnite(a.unite_id);
                    const artEmpl = emplacements[a.article_id] || [];
                    const sel = selectedIds.has(a.article_id);
                    const show = (k) => visibleCols[k] !== false;
                    return (
                      <tr key={a.article_id} data-testid="article-row"
                        onClick={() => openEdit(a)}
                        className={`cursor-pointer transition-colors ${sel ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}
                        style={{ borderBottom: '1px solid #F0F0EC', opacity: a.actif === false ? 0.5 : 1 }}>
                        {/* Checkbox */}
                        <td className="pl-4 pr-2 py-2.5 w-10" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={sel}
                            onChange={() => setSelectedIds(prev => {
                              const next = new Set(prev);
                              next.has(a.article_id) ? next.delete(a.article_id) : next.add(a.article_id);
                              return next;
                            })}
                            className="w-4 h-4 rounded cursor-pointer"
                            style={{ accentColor: 'var(--primary-solid)' }} />
                        </td>
                        {/* Picto image — aucune img chargée au scroll */}
                        <td className="px-1 py-2 w-10">
                          <div className="flex items-center justify-center">
                            {a.image_url ? (
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setImagePreview(a.image_url); }}
                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                title="Voir l'image"
                              >
                                <Camera size={14} style={{ color: 'var(--primary-solid)' }} />
                              </button>
                            ) : (
                              <ImageOff size={13} style={{ color: 'var(--text-disabled)' }} />
                            )}
                          </div>
                        </td>
                        {show('reference') && (
                          <td className="px-3 py-2.5">
                            <span className="font-mono text-xs px-2 py-0.5 rounded" style={{ background: 'var(--border-light)', color: '#555' }}>{a.reference}</span>
                          </td>
                        )}
                        {show('designation') && (
                          <td className="px-3 py-2.5" style={{ minWidth: 200 }}>
                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{a.designation}</p>
                            {a.description && <p className="text-xs truncate max-w-48" style={{ color: 'var(--text-placeholder)' }} title={a.description}>{a.description}</p>}
                          </td>
                        )}
                        {show('type') && tabType === 'tous' && (
                          <td className="px-3 py-2.5"><TypeBadge type={a.type} /></td>
                        )}
                        {show('famille') && (
                          <td className="px-3 py-2.5">
                            {famille ? (
                              <div>
                                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{famille.nom}</p>
                                {sousFam && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{sousFam.nom}</p>}
                              </div>
                            ) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                        )}
                        {show('marque') && (
                          <td className="px-3 py-2.5">
                            {marque ? (
                              <div>
                                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{marque.nom}</p>
                                {modele && <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>{modele.nom}</p>}
                              </div>
                            ) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                        )}
                        {show('emplacement') && (
                          <td className="px-3 py-2.5 text-center" style={{ minWidth: 140 }}>
                            {(() => {
                              // Filtrer par agence active (agences de l'utilisateur connecté)
                              const activeIds = activeAgences?.map(a => a.agence_id) || [];
                              const local = artEmpl.filter(e =>
                                activeIds.length === 0 || activeIds.includes(e.agence_id)
                              );
                              if (local.length === 0) return <span style={{ color: 'var(--text-disabled)' }}>—</span>;
                              return (
                                <div className="flex flex-col gap-1">
                                  {local.map((loc, li) => (
                                    <div key={li} className="flex flex-col gap-0.5">
                                      <div className="flex items-center gap-1 flex-wrap">
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                                          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', maxWidth: 130 }}
                                          title={loc.stock_nom}>
                                          <span className="truncate">{loc.stock_nom}</span>
                                        </span>
                                        {loc.zone_nom && (
                                          <span className="text-xs px-1 py-0.5 rounded"
                                            style={{ background: '#F0FDF4', color: '#15803D' }}>
                                            {loc.zone_nom}
                                          </span>
                                        )}
                                      </div>
                                      {loc.agence_nom && (
                                        <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>{loc.agence_nom}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                        {show('stock') && (
                          <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                            {a.type === 'materiel' ? (() => {
                              const activeIds = activeAgences?.map(ag => ag.agence_id) || [];
                              const localEmpl = activeIds.length > 0 ? artEmpl.filter(e => activeIds.includes(e.agence_id)) : artEmpl;
                              const localQty = localEmpl.reduce((s, e) => s + e.quantite, 0);
                              const stockEmpl = artEmpl.filter(e => e.quantite > 0);
                              const totalMulti = stockEmpl.reduce((s, e) => s + e.quantite, 0);
                              const hasOtherDepots = totalMulti > localQty;
                              return (
                                <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                                  <span className="text-xs font-semibold tabular-nums"
                                    style={{ color: localQty > 0 ? '#374151' : '#DC2626' }}>
                                    {localQty.toLocaleString('fr-FR')}
                                  </span>
                                  {totalMulti > 0 && (hasOtherDepots || stockEmpl.length > 1) && (
                                    <button
                                      title={`${totalMulti} unités au total dans tous les dépôts`}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setStockPopover(p =>
                                          p?.article?.article_id === a.article_id ? null
                                          : { article: a, emplacements: stockEmpl, rect }
                                        );
                                      }}
                                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold transition-all hover:opacity-80"
                                      style={{ background: '#DCFCE7', color: '#15803D' }}>
                                      <Building2 size={10} />
                                      {totalMulti.toLocaleString('fr-FR')}
                                    </button>
                                  )}
                                </div>
                              );
                            })() : a.gestion_stock ? (() => {
                              const activeIds = activeAgences?.map(ag => ag.agence_id) || [];
                              // Stock local = somme des emplacements de l'agence active (données fraîches)
                              const localEmpl = activeIds.length > 0
                                ? artEmpl.filter(e => activeIds.includes(e.agence_id))
                                : artEmpl;
                              const localQty = localEmpl.reduce((s, e) => s + e.quantite, 0);
                              // Total tous dépôts (uniquement entrées avec qty > 0 pour le popover)
                              const stockEmpl = artEmpl.filter(e => e.quantite > 0);
                              const totalMulti = stockEmpl.reduce((s, e) => s + e.quantite, 0);
                              const hasOtherDepots = totalMulti > localQty;

                              // Seuil effectif : agrégation par dépôt depuis stock_articles
                              // (remplace l'ancienne valeur globale a.quantite_min)
                              const depotMap = {};
                              localEmpl.forEach(e => {
                                if (!depotMap[e.stock_id]) depotMap[e.stock_id] = { qty: 0, min: null };
                                depotMap[e.stock_id].qty += e.quantite;
                                if (e.quantite_min != null && e.quantite_min > 0)
                                  depotMap[e.stock_id].min = e.quantite_min;
                              });
                              const depots = Object.values(depotMap);
                              const isLow = depots.some(d => d.min != null && d.qty <= d.min);
                              const effectiveMin = depots.reduce((m, d) => {
                                if (d.min == null) return m;
                                return m == null ? d.min : Math.min(m, d.min);
                              }, null);

                              return (
                                <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                                  <span className="text-xs font-semibold tabular-nums"
                                    style={{ color: isLow ? '#DC2626' : '#374151' }}>
                                    {localQty.toLocaleString('fr-FR')}
                                  </span>
                                  {effectiveMin != null && (
                                    <span className="text-xs" style={{ color: 'var(--text-placeholder)' }}>/ {effectiveMin}</span>
                                  )}
                                  {totalMulti > 0 && (hasOtherDepots || stockEmpl.length > 1) && (
                                    <button
                                      title={`${totalMulti} unités au total dans tous les dépôts`}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setStockPopover(p =>
                                          p?.article?.article_id === a.article_id ? null
                                          : { article: a, emplacements: stockEmpl, rect }
                                        );
                                      }}
                                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold transition-all hover:opacity-80"
                                      style={{ background: '#DCFCE7', color: '#15803D' }}>
                                      <Building2 size={10} />
                                      {totalMulti.toLocaleString('fr-FR')}
                                    </button>
                                  )}
                                </div>
                              );
                            })() : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                        )}
                        {show('unite') && (
                          <td className="px-3 py-2.5">
                            {unite ? (
                              <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: 'var(--border-light)', color: '#555' }}>{unite.symbole}</span>
                            ) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                        )}
                        {show('prix_vente') && (
                          <td className="px-3 py-2.5">
                            {a.prix_vente != null ? (
                              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#15803D' }}>
                                {formatPrice(Number(a.prix_vente))} €
                              </span>
                            ) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                          </td>
                        )}
                        {show('actif') && (
                          <td className="px-3 py-2.5">
                            {a.actif === false && (
                              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                style={{ background: '#FEF3C7', color: '#92400E' }}>
                                Archivé
                              </span>
                            )}
                          </td>
                        )}
                        {/* Colonne vide pour aligner avec le <th> du sélecteur */}
                        <td className="w-10" />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t flex items-center justify-between gap-4 flex-wrap"
              style={{ borderColor: 'var(--border)' }}>
              {/* Infos + sélection */}
              <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-placeholder)' }}>
                <span>
                  {fetchingArticles
                    ? 'Chargement…'
                    : `${Math.min((safePage - 1) * pageSize + 1, total)}–${Math.min(safePage * pageSize, total)} sur ${total} article${total !== 1 ? 's' : ''}`
                  }
                </span>
                {selectedIds.size > 0 && (
                  <span style={{ color: 'var(--primary-solid)' }}>· {selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}</span>
                )}
              </div>
              {/* Contrôles de pagination */}
              <div className="flex items-center gap-2">
                {/* Taille de page */}
                <select value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1 rounded-lg border text-xs"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                  {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                {/* Boutons de page */}
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage(1)} disabled={safePage === 1}
                    className="px-2 py-1 rounded text-xs disabled:opacity-30 hover:bg-gray-100 transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>«</button>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
                    className="px-2 py-1 rounded text-xs disabled:opacity-30 hover:bg-gray-100 transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>‹</button>
                  {/* Numéros de pages */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
                    .reduce((acc, n, i, arr) => {
                      if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((n, i) => n === '…'
                      ? <span key={`ellipsis-${i}`} className="px-1 text-xs" style={{ color: 'var(--text-placeholder)' }}>…</span>
                      : <button key={n} onClick={() => setPage(n)}
                          className="min-w-[28px] px-2 py-1 rounded text-xs font-medium transition-colors"
                          style={{
                            background: n === safePage ? 'var(--primary-solid)' : 'transparent',
                            color: n === safePage ? 'white' : 'var(--text-secondary)',
                          }}>{n}</button>
                    )}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                    className="px-2 py-1 rounded text-xs disabled:opacity-30 hover:bg-gray-100 transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>›</button>
                  <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages}
                    className="px-2 py-1 rounded text-xs disabled:opacity-30 hover:bg-gray-100 transition-colors"
                    style={{ color: 'var(--text-secondary)' }}>»</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ─── Side Panel ──────────────────────────────────────────────────────── */}
      <SidePanel
        open={panelOpen}
        onOpenChange={o => { if (!o) { setPanelOpen(false); closePopover(); } }}
        onPointerDownOutside={popover.open ? (e => e.preventDefault()) : undefined}
        size="lg"
        title={editRow ? editRow.reference : 'Nouvel article'}
        copyValue={editRow ? editRow.reference : undefined}
        subtitle={editRow ? 'Modifier l\'article' : 'Créer un nouvel article catalogue'}
        headerExtra={editRow ? (() => {
          const t = TYPES.find(x => x.value === editRow.type);
          if (!t) return null;
          return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: t.bg, color: t.color, flexShrink: 0 }}>
              <t.icon size={12} /> {t.label}
            </span>
          );
        })() : null}
        footer={
          <div className="flex items-center justify-between w-full">
            {editRow ? (
              <div className="flex items-center gap-2">
                {usage === null ? (
                  /* Chargement usage */
                  <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>…</span>
                ) : usage.peut_supprimer ? (
                  /* Jamais mouvementé → suppression définitive */
                  <button type="button" data-testid="btn-supprimer-article"
                    onClick={() => handleDelete(editRow.article_id)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                    style={{ color: '#DC2626', background: '#FEF2F2' }}>
                    <Trash2 size={13} /> Supprimer
                  </button>
                ) : (
                  /* Mouvementé → archivage */
                  <button type="button" data-testid="btn-archiver-article"
                    onClick={handleArchive}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium"
                    style={{ color: editRow.actif ? '#92400E' : '#15803D', background: editRow.actif ? '#FEF3C7' : '#F0FDF4' }}>
                    {editRow.actif ? <><Package size={13} /> Archiver</> : <><CheckCircle2 size={13} /> Réactiver</>}
                  </button>
                )}
              </div>
            ) : <span />}
            <div className="flex gap-3">
              <button type="button" onClick={() => setPanelOpen(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Annuler
              </button>
              {(panelTab === 'infos' || panelTab === 'description') && (
                <button data-testid="save-article-btn" type="button" onClick={handleSave} disabled={saving}
                  className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
                  style={{ background: saving ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
                  {saving ? 'Enregistrement...' : (editRow ? 'Mettre à jour' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Onglets — pour matériel ET pièce détachée existants */}
        {editRow && (editRow.type === 'materiel' || editRow.type === 'piece_detachee') && (
          <div className="flex border-b mb-4 overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
            {[
              { key: 'infos',         label: 'Informations',                                                    show: true },
              { key: 'description',   label: 'Description',                                                     show: true },
              { key: 'caracs',        label: 'Caractéristiques',                                                show: editRow.type === 'materiel' },
              { key: 'materiels',     label: `Matériels associés${materielsLies.length > 0 ? ` (${materielsLies.length})` : ''}`, show: editRow.type === 'materiel' },
              { key: 'equivalences',  label: 'Équivalences',                                                    show: true },
              { key: 'stock',         label: 'Suivi de stock ⚙',                                                show: true, disabled: true },
              { key: 'previsions',    label: 'Prévisions',                                                      show: true },
            ].filter(t => t.show).map(tab => (
              <button key={tab.key} type="button"
                onClick={() => {
                  if (tab.disabled) return;
                  setPanelTab(tab.key);
                  if (tab.key === 'previsions') loadPrevisions(editRow.article_id);
                  if (tab.key === 'equivalences') { /* chargé par le composant */ }
                }}
                data-testid={`article-tab-${tab.key}`}
                className="flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
                style={{
                  borderColor: panelTab === tab.key ? 'var(--primary-solid)' : 'transparent',
                  color: tab.disabled ? 'var(--text-disabled)' : panelTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                  cursor: tab.disabled ? 'not-allowed' : 'pointer',
                  opacity: tab.disabled ? 0.45 : 1,
                }}>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Onglet Caractéristiques */}
        {panelTab === 'caracs' && editRow ? (
          <ArticleCaracTab articleId={editRow.article_id} />
        ) : panelTab === 'stock' && editRow ? (
          /* ── Onglet Suivi de stock ───────────────────────────────── */
          <StockTab article={editRow} articleId={editRow.article_id} />
        ) : panelTab === 'equivalences' && editRow ? (
          <EquivalencesTab articleId={editRow.article_id} />
        ) : panelTab === 'description' && editRow ? (
          /* ── Onglet Description ──────────────────────────────────── */
          <DescriptionTab form={form} setForm={setForm} saving={saving} onSave={handleSave} />
        ) : panelTab === 'materiels' && editRow ? (
          /* ── Onglet Matériels associés ─────────────────────────────── */
          <div className="space-y-3 py-1">
            {loadingMateriels ? (
              <div className="flex items-center gap-2 text-xs px-1" style={{ color: 'var(--text-muted)' }}>
                <div className="animate-spin w-3.5 h-3.5 border-2 border-t-transparent rounded-full"
                  style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                Chargement des matériels...
              </div>
            ) : materielsLies.length === 0 ? (
              <p className="text-sm px-1" style={{ color: 'var(--text-placeholder)' }}>
                Aucun matériel rattaché. Pour lier un matériel à cet article, éditez la fiche matériel et sélectionnez cet article.
              </p>
            ) : (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                {/* En-tête */}
                <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 border-b"
                  style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Désignation</span>
                  <span className="text-xs font-semibold uppercase tracking-wide w-32 text-right" style={{ color: 'var(--text-muted)' }}>Exploitant</span>
                  <span className="text-xs font-semibold uppercase tracking-wide w-24 text-right" style={{ color: 'var(--text-muted)' }}>Statut</span>
                </div>
                {materielsLies.map((m, i) => (
                  <div key={m.materiel_id}
                    className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2.5"
                    style={{ borderBottom: i < materielsLies.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                    {/* Désignation + marque/modèle */}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                        {m.nom}
                        {m.numero_parc && <span className="ml-1.5 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>· N°{m.numero_parc}</span>}
                      </p>
                      {(m.marque || m.modele) && (
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {[m.marque, m.modele].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                    {/* Exploitant */}
                    <div className="w-32 text-right min-w-0">
                      <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{m.exploitant_nom || '—'}</p>
                    </div>
                    {/* Statut */}
                    <div className="w-24 flex justify-end">
                      <span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: 'var(--border-light)', color: 'var(--text-secondary)' }}>
                        {m.statut || '—'}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="px-4 py-2 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
                  {materielsLies.length} matériel{materielsLies.length > 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        ) : panelTab === 'previsions' && editRow ? (
          /* ── Onglet Prévisions ──────────────────────────────────── */
          <div className="space-y-3 py-1">
            {/* Seuils min par dépôt — en première position */}
            <div>
              <label className={lbl} style={lblSt}>
                Seuils min par dépôt
                <span className="ml-2 font-normal"
                  style={{ color: 'var(--text-placeholder)', textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>
                  déclencheurs d'approvisionnement
                </span>
              </label>
              <StockThresholdsPanel articleId={editRow.article_id} allStocks={allStocks} />
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Stock prévisionnel par dépôt — mis à jour au clic
              </p>
              <button type="button" onClick={() => loadPrevisions(editRow.article_id)} data-testid="previsions-refresh-btn"
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
                <RefreshCw size={11} /> Actualiser
              </button>
            </div>

            {loadingPrevisions ? (
              <div className="flex items-center gap-2 text-xs py-6 justify-center" style={{ color: 'var(--text-muted)' }}>
                <div className="animate-spin w-4 h-4 border-2 border-t-transparent rounded-full"
                  style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                Chargement des prévisions…
              </div>
            ) : previsions.length === 0 ? (
              <div className="rounded-xl py-10 text-center" style={{ border: '1px dashed var(--border)' }}>
                <Package size={28} className="mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucune donnée de stock pour cet article</p>
              </div>
            ) : (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse" data-testid="previsions-table">
                    <thead>
                      <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-light)' }}>
                        {['Agence', 'Physique', 'Rés. Ventes', 'Rés. OR', 'Cde Fourn.', 'Stock prévu'].map((h, idx) => (
                          <th key={h} className={`px-3 py-2 font-semibold uppercase tracking-wide whitespace-nowrap ${idx === 0 ? 'text-left' : 'text-center'}`}
                            style={{ color: 'var(--text-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previsions.map((p, i) => {
                        const color = p.disponible_net > 0 ? '#15803D' : p.disponible_net === 0 ? '#B45309' : '#DC2626';
                        const cellBtnSt = { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 };
                        return (
                          <tr key={p.agence_id || 'sans-agence'} data-testid="previsions-row"
                            style={{ borderBottom: i < previsions.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                            <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--text)' }}>{p.agence_nom || '—'}</td>
                            <td className="px-3 py-2.5 font-semibold tabular-nums text-center" style={{ color: 'var(--text)' }}>{p.physique}</td>
                            <td className="px-3 py-2.5 tabular-nums text-center">
                              {p.reservee_ventes > 0
                                ? <button type="button" onClick={e => openPopover('ventes', p.agence_id, p.agence_nom, e)}
                                    style={{ ...cellBtnSt, color: '#B45309' }} data-testid="popover-ventes-btn">
                                    -{p.reservee_ventes}
                                  </button>
                                : <span style={{ color: 'var(--text-placeholder)' }}>—</span>}
                            </td>
                            <td className="px-3 py-2.5 tabular-nums text-center">
                              {p.reservee_or > 0
                                ? <button type="button" onClick={e => openPopover('or', p.agence_id, p.agence_nom, e)}
                                    style={{ ...cellBtnSt, color: '#7C3AED' }} data-testid="popover-or-btn">
                                    -{p.reservee_or}
                                  </button>
                                : <span style={{ color: 'var(--text-placeholder)' }}>—</span>}
                            </td>
                            <td className="px-3 py-2.5 tabular-nums text-center">
                              {p.cde_fournisseur > 0
                                ? <button type="button" onClick={e => openPopover('achats', p.agence_id, p.agence_nom, e)}
                                    style={{ ...cellBtnSt, color: '#0369A1' }} data-testid="popover-achats-btn">
                                    +{p.cde_fournisseur}
                                  </button>
                                : <span style={{ color: 'var(--text-placeholder)' }}>—</span>}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className="font-bold tabular-nums px-2 py-0.5 rounded-full text-xs"
                                style={{ background: color + '18', color }}>
                                {p.disponible_net}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {previsions.length > 1 && (() => {
                      const totPhys = previsions.reduce((s, p) => s + p.physique, 0);
                      const totVentes = previsions.reduce((s, p) => s + p.reservee_ventes, 0);
                      const totOr = previsions.reduce((s, p) => s + p.reservee_or, 0);
                      const totAchats = previsions.reduce((s, p) => s + p.cde_fournisseur, 0);
                      const totNet = previsions.reduce((s, p) => s + p.disponible_net, 0);
                      const netColor = totNet > 0 ? '#15803D' : totNet === 0 ? '#B45309' : '#DC2626';
                      return (
                        <tfoot>
                          <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-subtle)' }}>
                            <td className="px-3 py-2 font-semibold text-xs" style={{ color: 'var(--text-muted)' }}>Total</td>
                            <td className="px-3 py-2 font-bold tabular-nums text-center" style={{ color: 'var(--text)' }}>{Math.round(totPhys * 1000) / 1000}</td>
                            <td className="px-3 py-2 font-bold tabular-nums text-center" style={{ color: totVentes > 0 ? '#B45309' : 'var(--text-placeholder)' }}>
                              {totVentes > 0 ? `-${Math.round(totVentes * 1000) / 1000}` : '—'}
                            </td>
                            <td className="px-3 py-2 font-bold tabular-nums text-center" style={{ color: totOr > 0 ? '#7C3AED' : 'var(--text-placeholder)' }}>
                              {totOr > 0 ? `-${Math.round(totOr * 1000) / 1000}` : '—'}
                            </td>
                            <td className="px-3 py-2 font-bold tabular-nums text-center" style={{ color: totAchats > 0 ? '#0369A1' : 'var(--text-placeholder)' }}>
                              {totAchats > 0 ? `+${Math.round(totAchats * 1000) / 1000}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <span className="font-bold tabular-nums px-2 py-0.5 rounded-full text-xs"
                                style={{ background: netColor + '18', color: netColor }}>
                                {Math.round(totNet * 1000) / 1000}
                              </span>
                            </td>
                          </tr>
                        </tfoot>
                      );
                    })()}
                  </table>
                </div>
                <div className="px-3 py-2 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
                  <span style={{ color: '#B45309' }}>■</span> Réservé Ventes &nbsp;
                  <span style={{ color: '#7C3AED' }}>■</span> Réservé OR &nbsp;
                  <span style={{ color: '#0369A1' }}>■</span> Cde Fournisseur &nbsp;· Cliquer sur un chiffre pour voir les documents
                </div>
              </div>
            )}
          </div>
        ) : (
        <div className="space-y-5">

          {/* ── Type ─────────────────────────────────────────── */}
          {!editRow && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
                style={{ color: 'var(--text-muted)',  }}>Type *</label>
              <div className="grid grid-cols-5 gap-2">
                {TYPES.map(t => (
                  <button key={t.value} type="button"
                    onClick={() => setForm(f => ({ ...f, type: t.value, marque_id: '', modele_id: '', famille_id: '', sous_famille_id: '', gestion_stock: false }))}
                    className="flex flex-col items-center py-2.5 rounded-xl border transition-all gap-1"
                    style={{
                      background: form.type === t.value ? t.bg : 'white',
                      color: form.type === t.value ? t.color : 'var(--text-muted)',
                      borderColor: form.type === t.value ? t.color : 'var(--border)',
                      fontWeight: form.type === t.value ? 600 : 400,
                    }}>
                    <t.icon size={16} />
                    <span className="text-xs">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Identification ────────────────────────────────── */}
          <div className="flex items-center gap-4">
            {/* Image 128×128 cliquable (mode édition uniquement) */}
            {editRow && (
              <div className="relative flex-shrink-0" style={{ width: 128, height: 128 }}>
                <label
                  htmlFor={`img-upload-${editRow.article_id}`}
                  className="relative rounded-xl overflow-hidden flex-shrink-0 cursor-pointer group block"
                  style={{ width: 128, height: 128, border: '1px solid var(--border)', background: editRow.image_url ? 'transparent' : 'var(--bg-subtle)' }}
                  title="Changer l'image"
                >
                  {editRow.image_url ? (
                    <img src={editRow.image_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageOff size={28} style={{ color: 'var(--text-disabled)' }} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                    <Camera size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <input
                    id={`img-upload-${editRow.article_id}`}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append('file', f);
                      try {
                        const r = await api.uploadArticleImage(editRow.article_id, fd);
                        setEditRow(prev => ({ ...prev, image_url: r.data.image_url }));
                        setArticles(prev => prev.map(a => a.article_id === editRow.article_id ? { ...a, image_url: r.data.image_url } : a));
                        toast.success('Image mise à jour');
                      } catch { toast.error("Erreur lors de l'upload"); }
                      e.target.value = '';
                    }}
                  />
                </label>
                {/* Bouton suppression image */}
                {editRow.image_url && (
                  <button
                    type="button"
                    title="Supprimer l'image"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        await api.deleteArticleImage(editRow.article_id);
                        setEditRow(prev => ({ ...prev, image_url: null }));
                        setArticles(prev => prev.map(a => a.article_id === editRow.article_id ? { ...a, image_url: null } : a));
                        toast.success('Image supprimée');
                      } catch { toast.error("Erreur lors de la suppression"); }
                    }}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm transition-colors hover:opacity-90 z-10"
                    style={{ background: '#EF4444', color: '#fff', fontSize: 10, lineHeight: 1 }}>
                    ×
                  </button>
                )}
              </div>
            )}
            {/* Référence + Unité + Désignation — grid pour aligner les inputs */}
            <div className="flex-1 min-w-0" style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr auto auto',
              rowGap: '1.25rem',
              columnGap: '0.5rem',
              alignItems: 'center',
            }}>
              {/* Ligne 1 : Référence | input | Unité label | Unité select */}
              <label className={lbl} style={lblSt}>Référence *</label>
              <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                placeholder="ex: REF-001" data-testid="article-reference-input"
                className={inp} style={inpSt} />
              <label className={lbl} style={{ ...lblSt, minWidth: 'auto', paddingLeft: '0.5rem' }}>
                Unité {!editRow && <span style={{ color: '#EF4444' }}>*</span>}
              </label>
              <div style={{ width: 150 }}>
                <select value={form.unite_id ?? ''} onChange={e => setForm(f => ({ ...f, unite_id: e.target.value }))}
                  data-testid="article-unite-select" className={inp} style={{ ...inpSt, width: '100%' }}>
                  <option value="">— Choisir —</option>
                  {unites.map(u => <option key={u.unite_id} value={u.unite_id}>{u.symbole} — {u.nom_fr}</option>)}
                </select>
              </div>
              {/* Ligne 2 : Désignation | input (colspan 3) */}
              <label className={lbl} style={lblSt}>Désignation *</label>
              <input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                placeholder="Nom de l'article" data-testid="article-designation-input"
                className={inp} style={{ ...inpSt, gridColumn: 'span 3' }} />
            </div>
          </div>

          {/* ── Classification ────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <label className={lbl} style={lblSt}>Famille</label>
              <div className="flex-1 min-w-0">
                <select value={form.famille_id} onChange={e => setForm(f => ({ ...f, famille_id: e.target.value, sous_famille_id: '' }))}
                  data-testid="article-famille-select" className={inp} style={inpSt}>
                  <option value="">— Aucune —</option>
                  {formFamilles.map(f => <option key={f.famille_id} value={f.famille_id}>{f.nom}</option>)}
                </select>
                {formFamilles.length === 0 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>Aucune famille pour ce type — <a href="/familles" className="underline">créer une famille</a></p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className={lbl} style={lblSt}>Sous-famille</label>
              <div className="flex-1 min-w-0">
                <select value={form.sous_famille_id} onChange={e => setForm(f => ({ ...f, sous_famille_id: e.target.value }))}
                  disabled={!form.famille_id || formSousFamilles.length === 0}
                  data-testid="article-sous-famille-select" className={inp} style={{ ...inpSt, opacity: !form.famille_id ? 0.5 : 1 }}>
                  <option value="">— Aucune —</option>
                  {formSousFamilles.map(sf => <option key={sf.sous_famille_id} value={sf.sous_famille_id}>{sf.nom}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── Marque / Modèle (conditionnel) ────────────────── */}
          {showsMarque(form.type) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <label className={lbl} style={lblSt}>Marque</label>
                <div className="flex-1 min-w-0">
                  {form.marque_id && selectedMarque ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                      style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-light)' }}>
                      <span className="flex-1 text-sm font-medium" style={{ color: 'var(--primary-solid)' }}>{selectedMarque.nom}</span>
                      <button type="button" onClick={() => setForm(f => ({ ...f, marque_id: '', modele_id: '' }))}
                        className="p-1 rounded hover:bg-red-50" style={{ color: '#DC2626' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative" ref={marqueRef}>
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
                      <input value={marqueSearch} onChange={e => { setMarqueSearch(e.target.value); setMarqueDropOpen(true); }}
                        onFocus={() => setMarqueDropOpen(true)}
                        placeholder="Rechercher marque..."
                        data-testid="article-marque-search"
                        className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none" style={inpSt} />
                      {marqueDropOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                          {filteredMarques.length === 0
                            ? <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun résultat</p>
                            : <div className="max-h-36 overflow-y-auto">
                                {filteredMarques.map(m => (
                                  <button key={m.marque_id} type="button"
                                    onMouseDown={() => { setForm(f => ({ ...f, marque_id: m.marque_id, modele_id: '' })); setMarqueSearch(''); setMarqueDropOpen(false); }}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm" style={{ borderBottom: '1px solid #F2F2EE' }}>
                                    {m.nom}
                                  </button>
                                ))}
                              </div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {showsModele(form.type) && (
                <div className="flex items-center gap-2">
                  <label className={lbl} style={lblSt}>Modèle</label>
                  <div className="flex-1 min-w-0">
                    <select value={form.modele_id} onChange={e => setForm(f => ({ ...f, modele_id: e.target.value }))}
                      disabled={!form.marque_id || formModeles.length === 0}
                      data-testid="article-modele-select" className={inp} style={{ ...inpSt, opacity: !form.marque_id ? 0.5 : 1 }}>
                      <option value="">— Aucun —</option>
                      {formModeles.map(m => <option key={m.modele_id} value={m.modele_id}>{m.nom}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Fournisseur préféré (sous Marque) ─────────────── */}
          <div className="flex items-center gap-2">
            <label className={lbl} style={lblSt}>Four. pref.</label>
            <div className="flex-1 min-w-0">
              {form.fournisseur_prefere?.nom ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                  style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-light)' }}>
                  <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--primary-solid)' }}>{form.fournisseur_prefere.nom}</span>
                  <button type="button" onClick={() => setForm(f => ({ ...f, fournisseur_prefere: null }))}
                    className="p-1 rounded hover:bg-red-50 flex-shrink-0" style={{ color: '#DC2626' }}>
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="relative" ref={fpRef}>
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
                  <input value={fpSearch}
                    onChange={e => { setFpSearch(e.target.value); setFpDropOpen(true); }}
                    onFocus={() => setFpDropOpen(true)}
                    placeholder="Rechercher fournisseur..."
                    data-testid="article-fournisseur-prefere"
                    className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none" style={inpSt} />
                  {fpDropOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                      {fournisseurs.filter(f => !fpSearch.trim() || (f.nom || '').toLowerCase().includes(fpSearch.toLowerCase())).length === 0
                        ? <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun résultat</p>
                        : <div className="max-h-36 overflow-y-auto">
                            {fournisseurs
                              .filter(f => !fpSearch.trim() || (f.nom || '').toLowerCase().includes(fpSearch.toLowerCase()))
                              .map(f => (
                                <button key={f.entreprise_id} type="button"
                                  onMouseDown={() => {
                                    setForm(fm => ({ ...fm, fournisseur_prefere: { ...(fm.fournisseur_prefere || {}), nom: f.nom } }));
                                    setFpSearch(''); setFpDropOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm" style={{ borderBottom: '1px solid #F2F2EE' }}>
                                  {f.nom}
                                </button>
                              ))}
                          </div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Stock (conditionnel) ──────────────────────────── */}
          {form.type === 'piece_detachee' && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'var(--bg-subtle)', border: '1px solid #E6E6E1' }}>
              <button type="button" onClick={() => setForm(f => ({ ...f, gestion_stock: !f.gestion_stock }))}
                data-testid="toggle-gestion-stock"
                className="flex items-center gap-2 text-sm font-medium">
                {form.gestion_stock
                  ? <ToggleRight size={22} style={{ color: 'var(--primary-solid)' }} />
                  : <ToggleLeft  size={22} style={{ color: 'var(--text-disabled)' }} />}
                Gestion en stock
              </button>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {form.gestion_stock ? 'Quantité suivie' : 'Non suivie en stock'}
              </span>
            </div>
          )}
          {form.type === 'materiel' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'var(--primary-50)', color: 'var(--primary-light)' }}>
              <ToggleRight size={16} /> Les matériels sont toujours gérés en stock
            </div>
          )}

          {formGestionStock && (() => {
            // Calcul du stock filtré par agence active (depuis emplacements déjà chargés)
            const artEmpl = emplacements[editRow?.article_id] || [];
            const activeIds = activeAgences?.map(ag => ag.agence_id) || [];
            const localEmpl = activeIds.length > 0
              ? artEmpl.filter(e => activeIds.includes(e.agence_id))
              : [];
            const localQty  = localEmpl.reduce((s, e) => s + (e.quantite || 0), 0);
            // Total toutes agences (uniquement entrées avec qty > 0)
            const stockEmpl = artEmpl.filter(e => (e.quantite || 0) > 0);
            const totalQty  = stockEmpl.reduce((s, e) => s + (e.quantite || 0), 0);
            const hasEmpl   = stockEmpl.length > 0;
            const hasAgence = activeIds.length > 0;
            return (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {/* Stock agence */}
                  <div className="flex items-center gap-2">
                    <label className={lbl} style={lblSt}>Stock agence</label>
                    <div className="flex-1 min-w-0">
                      {hasEmpl ? (
                        <div className={inp} style={{ ...inpSt, display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', cursor: 'default' }}>
                          <span className="font-semibold text-sm" data-testid="article-qte-stock-agence">
                            {hasAgence ? localQty.toLocaleString('fr-FR') : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                          </span>
                        </div>
                      ) : (
                        <div className={inp} style={{ ...inpSt, display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', cursor: 'default' }}>
                          <span className="font-semibold text-sm" data-testid="article-qte-stock-agence">
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stock total */}
                  <div className="flex items-center gap-2">
                    <label className={lbl} style={lblSt}>Stock total</label>
                    <div className="flex-1 min-w-0">
                      <div className={inp} style={{ ...inpSt, display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', cursor: 'default' }}>
                        <span className="font-semibold text-sm" data-testid="article-qte-stock-total">
                          {hasEmpl ? totalQty.toLocaleString('fr-FR') : <span style={{ color: 'var(--text-muted)' }}>0</span>}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contenant */}
                  <div className="flex items-center gap-2">
                    <label className={lbl} style={lblSt}>Contenant</label>
                    <div className="flex-1 min-w-0">
                      <select value={form.contenant_id ?? ''} onChange={e => setForm(f => ({ ...f, contenant_id: e.target.value }))}
                        data-testid="article-contenant-select" className={inp} style={inpSt}>
                        <option value="">— Aucun —</option>
                        {contenants.map(c => <option key={c.contenant_id} value={c.contenant_id}>{c.type} {c.contenance} {c.unite_ref}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Emplacements de stock */}
                <div>
                  <label className={lbl} style={lblSt}>
                    Emplacements
                    <span className="ml-2 font-normal"
                      style={{ color: 'var(--text-placeholder)', textTransform: 'none', letterSpacing: 'normal', fontSize: '10px' }}>
                      dépôt et zone de rangement
                    </span>
                  </label>
                  {editRow ? (
                    <ArticleEmplacementsPanel articleId={editRow.article_id} />
                  ) : (
                    <p className="text-xs px-3 py-2 rounded-lg" style={{ color: 'var(--text-placeholder)', background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
                      Enregistrez d&apos;abord l&apos;article pour voir les emplacements.
                    </p>
                  )}
                </div>
              </>
            );
          })()}

          {/* ── Tarif ────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3">
            {form.type !== 'prestation' && form.type !== 'main_oeuvre' && (
              <div className="flex items-center gap-2">
                <label className={lbl} style={lblSt}>
                  Prix d'achat (€)
                </label>
                <div
                  className="flex-1 min-w-0"
                  style={form.type === 'materiel' && !showPA ? {
                    filter: 'blur(5px)',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    transition: 'filter 0.2s ease',
                  } : { transition: 'filter 0.2s ease' }}>
                  <PriceInput value={form.prix_achat}
                    onChange={v => {
                      setForm(f => ({ ...f, prix_achat: v }));
                      triggerResolve(v, editRow?.article_id);
                    }}
                    data-testid="article-prix-achat-input" className={inp} style={inpSt} />
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className={lbl} style={lblSt}>Prix de vente (€)</label>
              <div className="flex-1 min-w-0">
                <PriceInput value={form.prix_vente}
                  onChange={v => { setForm(f => ({ ...f, prix_vente: v })); setResolvedPrixVente(null); }}
                  data-testid="article-prix-vente-input" className={inp} style={inpSt} />
                {resolvedPrixVente && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs"
                    style={{ color: 'var(--primary-solid)' }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: 'var(--primary-solid)' }} />
                    Matrice «{resolvedPrixVente.matrice_nom}» : ×{resolvedPrixVente.coeff} → <strong>{Number(resolvedPrixVente.prix_vente).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</strong>
                    <button
                      type="button"
                      onClick={() => { setForm(f => ({ ...f, prix_vente: String(resolvedPrixVente.prix_vente) })); setResolvedPrixVente(null); }}
                      className="ml-1 px-1.5 py-0.5 rounded text-xs font-semibold text-white"
                      style={{ background: 'var(--primary-solid)' }}
                      data-testid="apply-resolved-prix-vente">
                      Appliquer
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* PMP (depuis stock_articles) + Taux de marque */}
            {editRow?.prix_moyen_pondere != null && editRow.prix_moyen_pondere > 0 && (() => {
              const pmp = Number(editRow.prix_moyen_pondere);
              const pv  = Number(form.prix_vente) || 0;
              const txm = pv > 0 ? ((pv - pmp) / pv * 100) : null;
              return (
                <div className="rounded-xl px-3 py-2.5 space-y-1.5"
                  style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-light)' }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium" style={{ color: 'var(--text-muted)' }}>
                      PMP (stock réel)
                      <span className="ml-1 font-normal" style={{ fontSize: 10, color: 'var(--text-placeholder)' }}>prix moyen pondéré</span>
                    </span>
                    <span className="font-semibold tabular-nums" style={{ color: 'var(--text)' }}>
                      {pmp.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €
                    </span>
                  </div>
                  {txm !== null && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium" style={{ color: 'var(--text-muted)' }}>
                        Taux de marque
                        <span className="ml-1 font-normal" style={{ fontSize: 10, color: 'var(--text-placeholder)' }}>(PV − PMP) / PV</span>
                      </span>
                      <span className="font-bold tabular-nums"
                        style={{ color: txm >= 0 ? '#047857' : '#DC2626' }}>
                        {txm.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>


          

        </div>
        )} {/* fin condition panelTab !== 'caracs' */}
      </SidePanel>

      {/* ─── SidePanel Import ────────────────────────────────────────────────── */}
      <SidePanel
        open={importPanelOpen}
        onOpenChange={setImportPanelOpen}
        title="Importer des articles"
        subtitle="Fichier ODS ou XLSX — Nature = Matériel"
      >
        <div className="space-y-5">
          {/* Explications */}
          <div className="p-4 rounded-xl text-sm space-y-1"
            style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)', color: 'var(--primary-solid)' }}>
            <p className="font-semibold">Règles d'import :</p>
            <ul className="list-disc list-inside text-xs space-y-0.5" style={{ color: 'var(--text-muted)' }}>
              <li>Seules les lignes <strong>Nature = Matériel</strong> sont importées</li>
              <li>Si <strong>Modèle</strong> est vide → la valeur de <strong>Référence</strong> est utilisée</li>
              <li>Familles, sous-familles, marques et modèles sont créés automatiquement</li>
              <li>Les articles dont la référence existe déjà sont ignorés (idempotent)</li>
            </ul>
          </div>

          {/* Colonnes attendues */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Colonnes attendues dans le fichier</p>
            <div className="flex flex-wrap gap-1.5">
              {['Référence', 'Marque', 'Modèle', 'Nature', 'Famille', 'Sous-famille', 'Désignation'].map(col => (
                <span key={col} className="px-2 py-0.5 rounded text-xs font-mono"
                  style={{ background: '#F1F5F9', color: 'var(--text)', border: '1px solid var(--border)' }}>
                  {col}
                </span>
              ))}
            </div>
          </div>

          {/* Zone drag & drop */}
          {!importResult && (
            <div
              onDragOver={e => { e.preventDefault(); setImportDragging(true); }}
              onDragLeave={() => setImportDragging(false)}
              onDrop={e => { e.preventDefault(); setImportDragging(false); handleImportFileDrop(e.dataTransfer.files[0]); }}
              onClick={() => importInputRef.current?.click()}
              data-testid="import-dropzone"
              className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all"
              style={{
                borderColor: importDragging ? 'var(--primary-solid)' : importFile ? '#22C55E' : 'var(--border)',
                background: importDragging ? 'var(--primary-50)' : importFile ? '#F0FDF4' : '#FAFAFA',
              }}
            >
              {importFile ? (
                <div className="flex flex-col items-center gap-2">
                  <FileSpreadsheet size={32} style={{ color: '#22C55E' }} />
                  <p className="font-semibold text-sm" style={{ color: '#16A34A' }}>{importFile.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {(importFile.size / 1024).toFixed(0)} Ko — cliquer pour changer
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={30} style={{ color: importDragging ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
                  <p className="font-medium text-sm" style={{ color: importDragging ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
                    {importDragging ? 'Relâcher pour déposer' : 'Glisser-déposer un fichier ici'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>ou cliquer pour parcourir • .ods, .xlsx</p>
                </div>
              )}
              <input
                ref={importInputRef}
                type="file"
                accept=".ods,.xlsx,.xls"
                className="hidden"
                data-testid="import-file-input"
                onChange={e => handleImportFileDrop(e.target.files[0])}
              />
            </div>
          )}

          {/* Résultat */}
          {importResult && !importResult.error && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-xl"
                style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                <CheckCircle2 size={18} style={{ color: '#16A34A' }} />
                <p className="text-sm font-semibold" style={{ color: '#16A34A' }}>{importResult.message}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  { label: 'Articles créés', value: importResult.created, color: '#16A34A' },
                  { label: 'Ignorés (existants)', value: importResult.skipped, color: '#64748b' },
                  { label: 'Matériels liés', value: importResult.materiels_lies, color: '#7c3aed' },
                  { label: 'Familles créées', value: importResult.familles_creees, color: 'var(--primary-solid)' },
                  { label: 'Sous-familles créées', value: importResult.sous_familles_creees, color: 'var(--primary-solid)' },
                  { label: 'Marques créées', value: importResult.marques_creees, color: '#0ea5e9' },
                  { label: 'Modèles créés', value: importResult.modeles_crees, color: '#0ea5e9' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="p-2.5 rounded-lg" style={{ background: 'white', border: '1px solid var(--border)' }}>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</p>
                    <p className="text-xl font-bold" style={{ color }}>{value ?? 0}</p>
                  </div>
                ))}
              </div>
              {importResult.errors?.length > 0 && (
                <div className="p-3 rounded-xl text-xs space-y-1"
                  style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
                  <p className="font-semibold">{importResult.errors.length} erreur(s) :</p>
                  {importResult.errors.slice(0, 10).map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              <button
                onClick={() => { setImportFile(null); setImportResult(null); }}
                className="w-full py-2 rounded-lg text-sm font-medium"
                style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}
              >
                Faire un nouvel import
              </button>
            </div>
          )}

          {importResult?.error && (
            <div className="p-3 rounded-xl text-sm" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
              {importResult.error}
            </div>
          )}

          {/* Bouton importer */}
          {!importResult && (
            <button
              data-testid="run-import-btn"
              onClick={handleImport}
              disabled={importing || !importFile}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
              style={{ background: 'var(--primary-gradient)' }}
            >
              {importing
                ? <span className="flex items-center justify-center gap-2"><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Import en cours...</span>
                : <span className="flex items-center justify-center gap-2"><Upload size={15} /> Lancer l'import</span>
              }
            </button>
          )}
        </div>
      </SidePanel>

      {/* Panel de fusion d'articles */}
      {mergePanelOpen && selectedMaterielArticles.length === 2 && (
        <MergeArticlesPanel
          articles={selectedMaterielArticles}
          onClose={() => setMergePanelOpen(false)}
          onSuccess={() => {
            setSelectedIds(new Set());
            setMergePanelOpen(false);
            load(true);
            fetchArticles(1);
          }}
        />
      )}

      {/* Popover stock multi-agences */}
      {stockPopover && (
        <StockPopover
          article={stockPopover.article}
          emplacements={stockPopover.emplacements}
          rect={stockPopover.rect}
          onClose={() => setStockPopover(null)}
          onTransfert={({ article, stock }) => {
            setTransfertInit({ article, stock });
            setStockPopover(null);
          }}
        />
      )}

      {/* Modal demande de transfert — rapide depuis popover stock */}
      {transfertInit && (
        <TransfertQuickModal
          article={transfertInit.article}
          stockSource={transfertInit.stock}
          activeAgences={activeAgences}
          onClose={() => setTransfertInit(null)}
          navigate={navigate}
        />
      )}

      {/* Popover prévisions */}
      <PrevisionsPopover />

      {/* Modal prévisualisation image */}
      {imagePreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={() => setImagePreview(null)}
        >
          <div
            className="rounded-2xl overflow-hidden shadow-2xl"
            style={{ border: '2px solid var(--border)' }}
            onClick={e => e.stopPropagation()}
          >
            <img src={imagePreview} alt="" style={{ width: 256, height: 256, objectFit: 'contain', background: '#fff', display: 'block' }} />
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Onglet Équivalences ───────────────────────────────────────────────────────

function EquivalencesTab({ articleId }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug]     = useState(false);
  const [acting, setActing]       = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getArticleEquivalences(articleId);
      setItems(r.data || []);
    } catch { toast.error('Erreur chargement équivalences'); }
    finally { setLoading(false); }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  // Recherche articles — même logique que la barre /articles (paramètre `q`)
  useEffect(() => {
    if (search.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.getArticles({ q: search, limit: 50, sort_by: 'reference', sort_dir: 1 });
        const data = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setSuggestions(
          data.filter(a => a.article_id !== articleId && !items.find(i => i.article_id === a.article_id))
        );
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [search, articleId, items]);

  const handleAdd = async (article) => {
    setSearch('');
    setSuggestions([]);
    setShowSug(false);
    setActing('add');
    try {
      await api.addArticleEquivalence(articleId, { article_id_cible: article.article_id });
      toast.success(`${article.reference} ajouté comme équivalent`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setActing(null); }
  };

  const handleRemove = async (eqArticleId, ref) => {
    if (!window.confirm(`Retirer ${ref} des équivalents ?`)) return;
    setActing(eqArticleId);
    try {
      await api.removeArticleEquivalence(articleId, eqArticleId);
      toast.success('Équivalent retiré');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setActing(null); }
  };

  const handleTogglePrefere = async (eqArticleId, current) => {
    try {
      await api.patchArticleEquivalence(articleId, eqArticleId, { est_prefere: !current });
      setItems(prev => prev.map(i => i.article_id === eqArticleId ? { ...i, est_prefere: !current } : i));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 py-6 text-xs" style={{ color: 'var(--text-muted)' }}>
      <div className="animate-spin w-3.5 h-3.5 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      Chargement…
    </div>
  );

  return (
    <div className="space-y-3 py-1">
      {/* Tableau équivalents */}
      {items.length > 0 ? (
        <table className="w-full text-xs" data-testid="equivalences-table">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-light)', color: 'var(--text-muted)' }}>
              <th className="text-left pb-2 font-semibold">Référence</th>
              <th className="text-left pb-2 font-semibold">Désignation</th>
              <th className="text-right pb-2 font-semibold">Stock</th>
              <th className="text-right pb-2 font-semibold">Prix HT</th>
              <th className="text-center pb-2 font-semibold">Préféré</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.article_id} className="border-b" style={{ borderColor: 'var(--border-light)' }}
                data-testid={`eq-row-${item.article_id}`}>
                <td className="py-2 pr-2 font-medium" style={{ color: 'var(--text)' }}>{item.reference || '—'}</td>
                <td className="py-2 pr-2" style={{ color: 'var(--text-secondary)' }}>{item.designation}</td>
                <td className="py-2 pr-2 text-right tabular-nums" style={{ color: item.stock_total > 0 ? '#16a34a' : 'var(--text-muted)' }}>
                  {item.stock_total ?? 0}
                </td>
                <td className="py-2 pr-2 text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                  {item.prix_vente != null ? `${Number(item.prix_vente).toFixed(2)} €` : '—'}
                </td>
                <td className="py-2 text-center">
                  <button onClick={() => handleTogglePrefere(item.article_id, item.est_prefere)}
                    data-testid={`eq-prefere-${item.article_id}`}
                    title={item.est_prefere ? 'Retirer le statut préféré' : 'Marquer comme préféré'}
                    className="p-1 rounded transition-colors"
                    style={{ color: item.est_prefere ? '#d97706' : 'var(--text-disabled)' }}>
                    <Star size={13} fill={item.est_prefere ? 'currentColor' : 'none'} />
                  </button>
                </td>
                <td className="py-2">
                  <button onClick={() => handleRemove(item.article_id, item.reference)}
                    disabled={acting === item.article_id}
                    data-testid={`eq-remove-${item.article_id}`}
                    className="p-1 rounded hover:bg-red-50 transition-colors disabled:opacity-40"
                    style={{ color: '#dc2626' }}>
                    <X size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs py-2" style={{ color: 'var(--text-muted)' }}>Aucun équivalent défini pour cet article.</p>
      )}

      {/* Saisie fantôme — ajout d'équivalent */}
      <div className="relative" ref={inputRef}>
        <div className="flex items-center gap-2 border-t pt-3" style={{ borderColor: 'var(--border-light)' }}>
          <Plus size={13} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 200)}
            placeholder="Ajouter un équivalent — saisir référence ou désignation…"
            data-testid="equivalences-search-input"
            className="flex-1 text-xs py-1.5 px-2 rounded-lg border focus:outline-none focus:ring-1 focus:ring-blue-200"
            style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'white' }}
          />
        </div>
        {showSug && suggestions.length > 0 && (
          <div className="absolute left-0 top-full z-50 bg-white rounded-xl shadow-xl border overflow-hidden w-full max-h-56 overflow-y-auto"
            style={{ borderColor: 'var(--border)', marginTop: 2 }}>
            {suggestions.map(s => (
              <button key={s.article_id} type="button"
                onMouseDown={() => handleAdd(s)}
                data-testid={`eq-suggestion-${s.article_id}`}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b last:border-0 text-xs"
                style={{ borderColor: 'var(--border-light)' }}>
                <span className="font-semibold" style={{ color: 'var(--text)' }}>{s.reference}</span>
                <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>{s.designation}</span>
                {s.stock_total != null && (
                  <span className="ml-2" style={{ color: s.stock_total > 0 ? '#16a34a' : 'var(--text-disabled)' }}>
                    Stock : {s.stock_total}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
